import React, { useState, lazy, Suspense } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { MobileMotion, usePerformanceMonitor, useDeviceCapabilities, LazySection } from './components/MobileOptimizations'
import { useScrollRecovery, useMemoryOptimization, useCrashRecovery } from './components/CrashRecovery'
import { MobileOptimizedWrapper, useAggressiveMobileOptimizations } from './components/AggressiveMobileOptimizations'

// Mobile performance optimizations are handled by LazySection wrapper and MobileMotion components

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const shouldReduceMotion = useReducedMotion()
  const { isLowEnd } = useDeviceCapabilities()
  
  // Crash recovery and memory management
  useScrollRecovery()
  useCrashRecovery()
  const { registerCleanup } = useMemoryOptimization()
  const { isLowMemoryDevice, shouldDisableAnimations } = useAggressiveMobileOptimizations()
  
  // Monitor performance on mobile
  usePerformanceMonitor()
  
  // Determine if we should show simplified content for performance
  const showSimplifiedContent = isLowEnd || isLowMemoryDevice || window.innerWidth <= 768

  return (
    <>
      {/* Aurora/Prism Background Layer */}
      <div 
        className="fixed inset-0 aurora-background"
        style={{ zIndex: -100 }}
      />
    <div className="min-h-screen relative">

      {/* Navigation */}
      <MobileMotion 
        className="fixed w-full z-50 nav-adaptive border-b border-glass-border"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <motion.div 
              className="font-display font-bold text-xl lg:text-2xl bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent gradient-text-fix"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              HighPoint
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center space-x-6 xl:space-x-8 2xl:space-x-10">
                {[
                  { href: "#philosophy", label: "Philosophy" },
                  { href: "#technology", label: "Technology" },
                  { href: "#personas", label: "For You" },
                  { href: "#journey", label: "Journey" },
                  { href: "#business-functions", label: "Platform" },
                  { href: "#architecture", label: "Architecture" },
                  { href: "#comparison", label: "Compare" },
                  { href: "#creator", label: "About" },
                  { href: "#faq", label: "FAQ" }
                ].map((item) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    className="text-white hover:text-cyan-400 transition-all duration-300 font-medium text-sm xl:text-base relative group whitespace-nowrap"
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-300"></span>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <motion.button
                className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                whileTap={{ scale: 0.95 }}
                aria-label="Toggle mobile menu"
              >
                <motion.span
                  className="block w-5 h-0.5 bg-white mb-1"
                  animate={{
                    rotate: isMobileMenuOpen ? 45 : 0,
                    y: isMobileMenuOpen ? 6 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="block w-5 h-0.5 bg-white mb-1"
                  animate={{
                    opacity: isMobileMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="block w-5 h-0.5 bg-white"
                  animate={{
                    rotate: isMobileMenuOpen ? -45 : 0,
                    y: isMobileMenuOpen ? -6 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="lg:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-xl border-b border-white/10"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                <div className="grid gap-4">
                  {[
                    { href: "#philosophy", label: "Philosophy" },
                    { href: "#technology", label: "Technology" },
                    { href: "#personas", label: "For You" },
                    { href: "#journey", label: "Journey" },
                    { href: "#business-functions", label: "Platform" },
                    { href: "#architecture", label: "Architecture" },
                    { href: "#comparison", label: "Compare" },
                    { href: "#creator", label: "About" },
                    { href: "#faq", label: "FAQ" }
                  ].map((item, index) => (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      className="block py-3 px-4 rounded-lg text-white hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.label}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </MobileMotion>
      {/* Hero Section - Critical content, always render first */}
      <MobileOptimizedWrapper priority="high">
        <section className="pt-20 min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Simplified gradient orbs for mobile performance */}
        {!isLowEnd && (
          <>
            <MobileMotion
              className="absolute top-35 -left-48 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-2xl"
              animate={{
                x: [0, 50, 0],
                y: [0, -25, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <MobileMotion
              className="absolute bottom-20 -right-48 w-96 h-96 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-2xl"
              animate={{
                x: [0, -50, 0],
                y: [0, 50, 0],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </>
        )}
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-20">
          <MobileMotion 
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-8 text-brand-gradient leading-tight text-adaptive-heading gradient-text-fix mobile-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            The Future of
            <br />
            <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">Enterprise Management</span>
          </MobileMotion>
          <MobileMotion 
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-text-secondary mb-8 max-w-4xl mx-auto leading-relaxed text-adaptive mobile-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We're architecting the next generation of enterprise software – HighPoint will transform how organizations manage their operations, bringing intelligence and simplicity to complex business processes.
          </MobileMotion>
          <MobileMotion 
            className="text-base sm:text-lg md:text-xl mb-12 max-w-3xl mx-auto text-adaptive opacity-90 mobile-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Imagine enterprise software that actually works for you. HighPoint is being designed to eliminate vendor lock-in, remove black boxes, and deliver transparent orchestration that will adapt to your business – not force you to adapt to it.
          </MobileMotion>
          <MobileMotion 
            className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <button className="btn-primary px-10 py-4 rounded-xl text-lg font-semibold bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 transition-all duration-300 shadow-lg shadow-blue-500/25 mobile-button touch-optimized">
              Get Early Access
            </button>
            <button className="glass-light px-10 py-4 rounded-xl text-lg font-semibold text-text-primary border border-glass-border hover:border-brand-accent/30 transition-colors mobile-button touch-optimized">
              Learn More
            </button>
          </MobileMotion>
          
          {/* Key Features Grid */}
          <MobileMotion 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <MobileMotion 
              className="text-center mobile-card"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix mb-2 mobile-text">No-Code</div>
              <div className="text-sm text-text-tertiary font-medium text-adaptive mobile-text">Workflow Builder</div>
            </MobileMotion>
            <MobileMotion 
              className="text-center mobile-card"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix mb-2 mobile-text">API-First</div>
              <div className="text-sm text-text-tertiary font-medium text-adaptive mobile-text">Architecture</div>
            </MobileMotion>
            <MobileMotion 
              className="text-center mobile-card"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-300 bg-clip-text text-transparent gradient-text-fix mb-2 mobile-text">Real-Time</div>
              <div className="text-sm text-text-tertiary font-medium text-adaptive mobile-text">Data Sync</div>
            </MobileMotion>
            <MobileMotion 
              className="text-center mobile-card"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix mb-2 mobile-text">Multi-Tenant</div>
              <div className="text-sm text-text-tertiary font-medium text-adaptive mobile-text">Architecture</div>
            </MobileMotion>
          </MobileMotion>
        </div>
        </section>
      </MobileOptimizedWrapper>
      {/* Platform Philosophy Section - High priority */}
      <MobileOptimizedWrapper priority="high">
        <section id="philosophy" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <MobileMotion 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              The HighPoint Philosophy
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive">
              We're not building just another platform. We're reimagining what enterprise software 
              could become when designed from the ground up with modern technology and human-centered principles.
            </p>
          </MobileMotion>

          <div className="grid lg:grid-cols-2 gap-12 mb-20">
            {/* Design Principles - Enhanced Data Controls */}
            <motion.div 
              className="relative group"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-indigo-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div 
                className="relative rounded-3xl p-[1px] overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                }}
              >
                <div className="relative bg-black backdrop-blur-xl rounded-3xl p-10 overflow-hidden">
                <h3 className="text-2xl font-bold mb-8 text-adaptive flex items-center gap-3">
                  <span className="text-3xl">🎨</span>
                  Design Principles
                </h3>
                
                <div className="space-y-4">
                  {[
                    {
                      principle: "Human-First Design",
                      description: "Every interface, workflow, and interaction will be designed for humans who have better things to do than fight with software.",
                      metric: "100%",
                      label: "User-Centric",
                      color: "from-violet-500 to-purple-400"
                    },
                    {
                      principle: "Intelligent by Default",
                      description: "AI won't be a feature – it will be woven into the fabric. The platform will learn, adapt, and improve without you lifting a finger.",
                      metric: "24/7",
                      label: "Learning",
                      color: "from-indigo-500 to-blue-400"
                    },
                    {
                      principle: "Radically Transparent",
                      description: "No black boxes. You'll see exactly what's happening, why decisions are made, and how data flows through your system.",
                      metric: "Full",
                      label: "Visibility",
                      color: "from-purple-500 to-pink-400"
                    },
                    {
                      principle: "Infinitely Flexible",
                      description: "Your business is unique. HighPoint will mold itself to your processes, not force you to adapt to ours.",
                      metric: "∞",
                      label: "Adaptable",
                      color: "from-blue-500 to-cyan-400"
                    }
                  ].map((item, i) => (
                    <motion.div 
                      key={item.principle}
                      className="relative overflow-hidden rounded-xl bg-black/30 p-4"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-5`} />
                      
                      <div className="relative">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-violet-300">{item.principle}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix`}>
                              {item.label}
                            </span>
                            <span className="text-lg font-bold text-adaptive">{item.metric}</span>
                          </div>
                        </div>
                        <p className="text-sm text-text-secondary text-adaptive">{item.description}</p>
                      </div>
                      
                      <motion.div
                        className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${item.color}`}
                        initial={{ width: "0%" }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 1, delay: i * 0.2 }}
                        viewport={{ once: true }}
                      />
                    </motion.div>
                  ))}
                </div>
                </div>
              </div>
            </motion.div>

            {/* Technical Philosophy - Enhanced Data Controls */}
            <motion.div 
              className="relative group"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-violet-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div 
                className="relative rounded-3xl p-[1px] overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                }}
              >
                <div className="relative bg-black backdrop-blur-xl rounded-3xl p-10 overflow-hidden">
                <h3 className="text-2xl font-bold mb-8 text-adaptive flex items-center gap-3">
                  <span className="text-3xl">⚡</span>
                  Technical Excellence
                </h3>
                
                <div className="space-y-4">
                  {[
                    {
                      aspect: "Cloud-Native from Day One",
                      detail: "Being designed for the cloud from the ground up. Will scale infinitely, update seamlessly, and cost less to run.",
                      status: "Active",
                      indicator: "●",
                      color: "from-emerald-500 to-teal-400"
                    },
                    {
                      aspect: "Event-Driven Architecture",
                      detail: "Everything will be an event. Complete auditability, time-travel debugging, and perfect system state reconstruction.",
                      status: "CQRS",
                      indicator: "◆",
                      color: "from-cyan-500 to-blue-400"
                    },
                    {
                      aspect: "API-First Everything",
                      detail: "If you can see it, you'll be able to API it. Every feature, every function, accessible programmatically.",
                      status: "RESTful",
                      indicator: "▲",
                      color: "from-blue-500 to-indigo-400"
                    },
                    {
                      aspect: "Security as Foundation",
                      detail: "Zero-trust won't be a buzzword here. It's how we're building every component from the ground up.",
                      status: "Zero-Trust",
                      indicator: "■",
                      color: "from-indigo-500 to-purple-400"
                    }
                  ].map((item, i) => (
                    <motion.div 
                      key={item.aspect}
                      className="relative overflow-hidden rounded-xl bg-black/30 p-4"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-5`} />
                      
                      <div className="relative">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-indigo-300">{item.aspect}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`text-lg bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix`}>
                              {item.indicator}
                            </span>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full bg-gradient-to-r ${item.color} bg-opacity-10 text-adaptive`}>
                              {item.status}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-text-secondary text-adaptive">{item.detail}</p>
                      </div>
                      
                      <motion.div
                        className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${item.color}`}
                        initial={{ width: "0%" }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 1, delay: i * 0.2 }}
                        viewport={{ once: true }}
                      />
                    </motion.div>
                  ))}
                </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Innovation Showcase - Enhanced Grid */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-8 text-center bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent gradient-text-fix">
              Innovations That Will Set HighPoint Apart
            </h3>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {[
                {
                  innovation: "Time Machine for Data",
                  icon: "⏰",
                  description: "You'll see your entire system state at any point in history. Debug issues by rewinding time. Compliance made trivial.",
                  highlight: "Event Sourcing",
                  color: "from-amber-500 to-orange-400"
                },
                {
                  innovation: "Self-Documenting Workflows",
                  icon: "📝",
                  description: "As you build workflows, HighPoint will automatically generate documentation, training materials, and compliance reports.",
                  highlight: "Auto-Generated",
                  color: "from-rose-500 to-pink-400"
                },
                {
                  innovation: "Predictive Process Mining",
                  icon: "🔮",
                  description: "AI will analyze your processes in real-time, suggesting optimizations before inefficiencies become problems.",
                  highlight: "AI-Powered",
                  color: "from-purple-500 to-indigo-400"
                },
                {
                  innovation: "Natural Language Queries",
                  icon: "💬",
                  description: "You'll ask questions in plain English. 'Show me orders delayed by supplier issues last quarter.' Done.",
                  highlight: "NLP Engine",
                  color: "from-blue-500 to-cyan-400"
                },
                {
                  innovation: "Autonomous Optimization",
                  icon: "🤖",
                  description: "HighPoint will continuously tune itself for performance. Database indexes, query optimization, caching – all automatic.",
                  highlight: "Self-Tuning",
                  color: "from-emerald-500 to-teal-400"
                },
                {
                  innovation: "Universal Translator",
                  icon: "🌐",
                  description: "Connect any system, any format, any protocol. Our AI will figure out the mapping and keep data in sync.",
                  highlight: "Any-to-Any",
                  color: "from-indigo-500 to-violet-400"
                }
              ].map((item, i) => (
                <motion.div
                  key={item.innovation}
                  className="relative group"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-20 rounded-2xl blur-xl transition-opacity duration-500`} />
                  
                  <div 
                    className="relative rounded-2xl p-[1px] h-full"
                    style={{
                      background: `linear-gradient(135deg, ${
                        item.color === 'from-amber-500 to-orange-400' ? '#f59e0b, #fb923c' :
                        item.color === 'from-rose-500 to-pink-400' ? '#f43f5e, #fb7185' :
                        item.color === 'from-purple-500 to-indigo-400' ? '#a855f7, #818cf8' :
                        item.color === 'from-blue-500 to-cyan-400' ? '#3b82f6, #22d3ee' :
                        item.color === 'from-emerald-500 to-teal-400' ? '#10b981, #2dd4bf' :
                        '#6366f1, #8b5cf6'
                      })`
                    }}
                  >
                    <div className="relative bg-black backdrop-blur-xl rounded-2xl p-6 text-center h-full">
                      <motion.div 
                        className="text-5xl mb-4 opacity-90"
                        whileHover={{ scale: 1.05, rotate: 2 }}
                        transition={{ duration: 0.3 }}
                      >
                        {item.icon}
                      </motion.div>
                    
                    <h4 className="text-lg font-bold text-adaptive mb-2">{item.innovation}</h4>
                    
                    <span className={`inline-block text-sm font-bold px-6 py-2 rounded-full bg-gradient-to-r ${item.color} bg-opacity-20 text-adaptive mb-3 shadow-lg`} style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)' }}>
                      {item.highlight}
                    </span>
                    
                    <p className="text-sm text-text-secondary text-adaptive">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
        </section>
      </MobileOptimizedWrapper>
      {/* Technology Showcase Section - Medium priority */}
      <MobileOptimizedWrapper priority="medium">
        <LazySection>
          <section id="technology" className="py-32 relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              Next-Gen Tech That Actually Exists
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive mb-4">
              While others are still PowerPointing about "digital transformation," 
              we're shipping features that feel like science fiction.
            </p>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto text-adaptive opacity-90">
              (Except they're real, tested, and running in production today.)
            </p>
          </motion.div>

          {/* AI That Does Real Work - Redesigned */}
          <div className="mb-32">
            <motion.div 
              className="text-center mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.h3 
                className="text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-cyan-300 to-purple-400 bg-clip-text text-transparent gradient-text-fix"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                AI That Does Real Work
              </motion.h3>
              <motion.p 
                className="text-xl text-white max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Beyond buzzwords and hype. These are the AI capabilities that will actually move the needle in your business.
              </motion.p>
            </motion.div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {[
                {
                  title: "Predictive Process Optimization",
                  desc: "HighPoint learns your patterns and predicts bottlenecks before they happen. It's like having a crystal ball, except it works.",
                  icon: "🔮",
                  features: ["Anomaly detection", "Resource forecasting", "Preventive alerts"],
                  color: "from-blue-500 to-cyan-400",
                  accent: "blue"
                },
                {
                  title: "Natural Language Workflows",
                  desc: "Tell HighPoint what you want in plain English. 'Route urgent orders to Sarah when inventory is low.' Done.",
                  icon: "💬",
                  features: ["Voice commands", "Context understanding", "Multi-language support"],
                  color: "from-purple-500 to-pink-400",
                  accent: "purple"
                },
                {
                  title: "Self-Healing Processes",
                  desc: "When something breaks, HighPoint fixes it. Automatically. Then tells you what happened and why.",
                  icon: "🤖",
                  features: ["Auto-remediation", "Root cause analysis", "Learning from fixes"],
                  color: "from-emerald-500 to-teal-400",
                  accent: "emerald"
                },
                {
                  title: "Intelligent Document Processing",
                  desc: "Upload any document – PDF, image, handwritten notes. HighPoint extracts data and routes it correctly. Magic? Close enough.",
                  icon: "📄",
                  features: ["OCR + AI extraction", "Smart validation", "Auto-classification"],
                  color: "from-orange-500 to-red-400",
                  accent: "orange"
                }
              ].map((feature, i) => (
                <motion.div
                  key={feature.title}
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  {/* Clean content layout */}
                  <div className="relative p-8 h-full">
                    {/* Icon and title section */}
                    <div className="flex items-start gap-6 mb-8">
                      <motion.div 
                        className="text-4xl"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        {feature.icon}
                      </motion.div>
                      
                      <div className="flex-1">
                        <motion.h4 
                          className={`text-2xl font-bold mb-4 bg-gradient-to-r ${feature.color} bg-clip-text text-transparent gradient-text-fix`}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: i * 0.1 + 0.2 }}
                          viewport={{ once: true }}
                        >
                          {feature.title}
                        </motion.h4>
                        <motion.p 
                          className="text-white text-lg leading-relaxed"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: i * 0.1 + 0.3 }}
                          viewport={{ once: true }}
                        >
                          {feature.desc}
                        </motion.p>
                      </div>
                    </div>
                    
                    {/* Features as buttons in 2>1 layout */}
                    <div className="flex flex-wrap justify-center gap-3">
                      {feature.features.map((f, j) => (
                        <motion.button
                          key={j}
                          className="px-4 py-2 rounded-full text-sm font-semibold text-white border border-white/20 hover:border-white/40 transition-all duration-300"
                          style={{
                            background: `linear-gradient(135deg, ${feature.color.includes('blue') ? '#2563eb, #0891b2' : 
                              feature.color.includes('purple') ? '#7c3aed, #be185d' :
                              feature.color.includes('emerald') ? '#059669, #0d9488' :
                              '#ea580c, #dc2626'})`,
                            boxShadow: `0 4px 12px ${feature.color.includes('blue') ? 'rgba(37, 99, 235, 0.3)' : 
                              feature.color.includes('purple') ? 'rgba(124, 58, 237, 0.3)' :
                              feature.color.includes('emerald') ? 'rgba(5, 150, 105, 0.3)' :
                              'rgba(234, 88, 12, 0.3)'}, 0 0 15px ${feature.color.includes('blue') ? 'rgba(8, 145, 178, 0.15)' : 
                              feature.color.includes('purple') ? 'rgba(190, 24, 93, 0.15)' :
                              feature.color.includes('emerald') ? 'rgba(13, 148, 136, 0.15)' :
                              'rgba(220, 38, 38, 0.15)'}`
                          }}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ delay: i * 0.1 + j * 0.05 + 0.4, duration: 0.3 }}
                          viewport={{ once: true }}
                        >
                          {f}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Security Features */}
          <div className="mb-20">
            <motion.h3 
              className="text-3xl font-bold mb-12 text-center bg-gradient-to-r from-red-400 to-orange-300 bg-clip-text text-transparent gradient-text-fix"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              Security That Doesn't Get in the Way
            </motion.h3>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Zero Trust Architecture",
                  desc: "Every request verified. Every action logged. Every byte encrypted.",
                  icon: "🔒",
                  stat: "AES-256",
                  progress: 100,
                  color: "from-red-500 to-orange-400"
                },
                {
                  title: "NIST 800-171 Compliant",
                  desc: "We check all 110 boxes so you can sleep at night.",
                  icon: "🛡",
                  stat: "110/110",
                  progress: 100,
                  color: "from-orange-500 to-amber-400"
                },
                {
                  title: "Real-time Threat Detection",
                  desc: "AI-powered security that spots anomalies instantly.",
                  icon: "👀",
                  stat: "<1ms",
                  progress: 95,
                  color: "from-amber-500 to-yellow-400"
                }
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  className="relative group"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-20 rounded-2xl blur-xl transition-opacity duration-500`} />
                  
                  <div 
                    className="relative rounded-2xl p-[1px] h-full"
                    style={{
                      background: `linear-gradient(135deg, ${
                        item.color === 'from-red-500 to-orange-400' ? '#ef4444, #fb923c' :
                        item.color === 'from-orange-500 to-amber-400' ? '#f97316, #fbbf24' :
                        '#f59e0b, #facc15'
                      })`
                    }}
                  >
                    <div className="relative bg-black backdrop-blur-xl rounded-2xl p-6 text-center h-full">
                      <motion.div 
                        className="text-5xl mb-4"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        {item.icon}
                      </motion.div>
                    <h4 className="text-lg font-bold text-adaptive mb-2">{item.title}</h4>
                    <p className="text-sm text-text-secondary text-adaptive mb-4">{item.desc}</p>
                    
                    <div className="relative w-full h-2 bg-black/50 rounded-full overflow-hidden mb-3">
                      <motion.div
                        className={`absolute left-0 top-0 h-full bg-gradient-to-r ${item.color}`}
                        initial={{ width: "0%" }}
                        whileInView={{ width: `${item.progress}%` }}
                        transition={{ duration: 1.5, delay: i * 0.2 }}
                        viewport={{ once: true }}
                      />
                    </div>
                    
                    <div className={`text-2xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix`}>
                      {item.stat}
                    </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Integration Ecosystem - Enhanced Data Grid */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
              Connects to Everything You Already Use
            </h3>
            <p className="text-center text-text-secondary text-adaptive mb-12 max-w-3xl mx-auto">
              Pre-built connectors for every major platform. If it has an API, we can talk to it. 
              If it doesn't, we'll figure it out anyway.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 px-2">
              {[
                // Blue/Cyan Group - Business & Communication
                { name: "CRM Systems", icon: "🤝", color: "from-blue-400 to-cyan-300" },
                { name: "Chat Tools", icon: "💬", color: "from-blue-500 to-cyan-400" },
                { name: "Office Suites", icon: "💼", color: "from-cyan-400 to-blue-300" },
                { name: "Project Mgmt", icon: "🎯", color: "from-cyan-500 to-blue-400" },
                { name: "Service Desks", icon: "🎫", color: "from-blue-600 to-cyan-300" },
                { name: "BI Tools", icon: "📈", color: "from-cyan-600 to-blue-300" },
                
                // Green/Teal Group - Infrastructure & Data
                { name: "Databases", icon: "🗄", color: "from-emerald-400 to-teal-300" },
                { name: "Cloud Providers", icon: "☁️", color: "from-teal-400 to-emerald-300" },
                { name: "IoT Platforms", icon: "📡", color: "from-emerald-500 to-teal-400" },
                { name: "Monitoring", icon: "👀", color: "from-teal-500 to-emerald-300" },
                { name: "Custom APIs", icon: "🔧", color: "from-emerald-600 to-teal-400" },
                { name: "Warehouses", icon: "📦", color: "from-teal-600 to-emerald-300" },
                
                // Purple/Indigo Group - Enterprise & Development
                { name: "ERP Platforms", icon: "🏭", color: "from-purple-400 to-indigo-300" },
                { name: "Security Tools", icon: "🔒", color: "from-indigo-500 to-purple-400" },
                { name: "Version Control", icon: "🐱", color: "from-purple-500 to-indigo-300" },
                
                // Orange/Amber Group - Financial & Operations
                { name: "Accounting", icon: "💰", color: "from-orange-400 to-amber-300" },
                { name: "HR Systems", icon: "👥", color: "from-amber-500 to-orange-400" },
                { name: "E-commerce", icon: "🛒", color: "from-orange-500 to-amber-300" }
              ].map((integration, i) => (
                <motion.div
                  key={integration.name}
                  className="relative group p-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.02 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute inset-1 bg-gradient-to-br ${integration.color} opacity-0 group-hover:opacity-20 rounded-xl blur-md transition-opacity duration-300`} />
                  
                  <div 
                    className="relative rounded-xl p-[1px] h-full"
                    style={{
                      background: `linear-gradient(135deg, ${
                        integration.color.includes('blue') ? '#3b82f6, #22d3ee' :
                        integration.color.includes('cyan') ? '#06b6d4, #3b82f6' :
                        integration.color.includes('emerald') ? '#10b981, #14b8a6' :
                        integration.color.includes('teal') ? '#14b8a6, #10b981' :
                        integration.color.includes('purple') ? '#a855f7, #6366f1' :
                        integration.color.includes('indigo') ? '#6366f1, #a855f7' :
                        integration.color.includes('orange') ? '#f97316, #fbbf24' :
                        '#fbbf24, #f97316'
                      })`
                    }}
                  >
                    <div className="relative bg-black backdrop-blur-xl rounded-xl p-6 text-center h-full hover:border-white/20 transition-all duration-300 overflow-hidden">
                    <motion.div 
                      className="text-4xl mb-3 brightness-110"
                      whileHover={{ scale: 1.1, rotate: 2 }}
                      animate={{ 
                        y: [0, -1, 0],
                        rotate: [0, 0.5, -0.5, 0]
                      }}
                      transition={{ 
                        duration: 6,
                        repeat: Infinity,
                        delay: i * 0.2,
                        ease: "easeInOut"
                      }}
                    >
                      {integration.icon}
                    </motion.div>
                    <div className="text-sm font-bold text-white">
                      {integration.name}
                    </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-text-secondary text-adaptive">
                Plus custom connectors via our Integration SDK
              </span>
            </motion.div>
          </motion.div>
        </div>
        </section>
        </LazySection>
      </MobileOptimizedWrapper>
      {/* User Personas Section - Content for All Levels */}
      <section id="personas" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-indigo-400 to-purple-300 bg-clip-text text-transparent gradient-text-fix">
              Built for Everyone in Your Organization
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive">
              From the C-suite to the shop floor, HighPoint empowers every role with tools that 
              actually make sense for their daily work.
            </p>
          </motion.div>

          {/* Executive Level */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent gradient-text-fix">
              For Executives: Strategic Command Center
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  role: "CEO",
                  icon: "👑",
                  title: "Unified Business Intelligence",
                  features: [
                    "Real-time P&L visibility across all divisions",
                    "Predictive analytics for market opportunities",
                    "One-click drill-down from KPIs to root causes",
                    "Board-ready reports generated automatically"
                  ],
                  benefit: "Make decisions with complete confidence, backed by real-time data from every corner of your business."
                },
                {
                  role: "CFO",
                  icon: "💰",
                  title: "Financial Control Tower",
                  features: [
                    "Automated financial consolidation",
                    "Real-time cash flow forecasting",
                    "Compliance tracking with audit trails",
                    "Cost center analysis with AI insights"
                  ],
                  benefit: "Close the books faster, forecast with precision, and spot financial opportunities before competitors."
                },
                {
                  role: "COO",
                  icon: "⚙️",
                  title: "Operations Command",
                  features: [
                    "End-to-end process visibility",
                    "Bottleneck prediction and prevention",
                    "Resource optimization algorithms",
                    "Cross-functional workflow orchestration"
                  ],
                  benefit: "Orchestrate operations like a symphony conductor, with every department in perfect harmony."
                }
              ].map((exec, i) => (
                <motion.div
                  key={exec.role}
                  className="bg-gradient-to-br from-amber-950/20 to-yellow-950/20 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02, borderColor: "rgba(251, 191, 36, 0.3)" }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-4xl">{exec.icon}</div>
                    <div>
                      <div className="text-sm text-amber-400 font-semibold">{exec.role}</div>
                      <h4 className="text-xl font-bold text-adaptive">{exec.title}</h4>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {exec.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-text-secondary text-adaptive">
                        <span className="text-amber-400 mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <p className="text-sm text-adaptive font-medium italic border-t border-white/10 pt-4">
                    {exec.benefit}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Management Level */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix">
              For Managers: Intelligent Team Orchestration
            </h3>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {[
                {
                  role: "Department Managers",
                  icon: "📊",
                  scenarios: [
                    {
                      situation: "Monday Morning Planning",
                      solution: "HighPoint shows you exactly what needs attention today, prioritized by impact. See team capacity, project status, and potential issues in one view."
                    },
                    {
                      situation: "Resource Allocation",
                      solution: "AI suggests optimal team assignments based on skills, availability, and workload. Drag-and-drop to adjust, and see impact predictions instantly."
                    },
                    {
                      situation: "Performance Reviews",
                      solution: "Automated performance dashboards with objective metrics. No more spreadsheet hunting – everything is tracked and visualized."
                    }
                  ]
                },
                {
                  role: "Project Managers",
                  icon: "🎯",
                  scenarios: [
                    {
                      situation: "Multi-Project Juggling",
                      solution: "See all projects on one timeline with dependencies highlighted. HighPoint warns you about resource conflicts before they happen."
                    },
                    {
                      situation: "Stakeholder Updates",
                      solution: "Auto-generated status reports pull real data from all systems. Customize once, use forever. Stakeholders stay informed without the chase."
                    },
                    {
                      situation: "Risk Management",
                      solution: "Predictive risk scoring based on historical data. Get alerts when projects show early warning signs of delay or budget overrun."
                    }
                  ]
                }
              ].map((manager, i) => (
                <motion.div
                  key={manager.role}
                  className="bg-gradient-to-br from-blue-950/20 to-cyan-950/20 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-8"
                  initial={{ opacity: 0, x: i === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="text-3xl">{manager.icon}</div>
                    <h4 className="text-xl font-bold text-adaptive">{manager.role}</h4>
                  </div>
                  
                  <div className="space-y-4">
                    {manager.scenarios.map((scenario, j) => (
                      <div key={j} className="bg-black/30 rounded-xl p-4">
                        <div className="font-semibold text-blue-300 mb-2">{scenario.situation}</div>
                        <p className="text-sm text-text-secondary text-adaptive">{scenario.solution}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Operational Level */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
              For Operations: Tools That Actually Help
            </h3>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {[
                {
                  role: "Shop Floor Supervisors",
                  icon: "🏭",
                  title: "Production at Your Fingertips",
                  tools: [
                    "Live production dashboards on any device",
                    "One-touch quality issue reporting",
                    "Real-time OEE tracking",
                    "Shift handover automation"
                  ],
                  quote: "Finally, software that speaks our language. No more paper forms or Excel gymnastics."
                },
                {
                  role: "Field Service Technicians",
                  icon: "🔧",
                  title: "Everything You Need, Anywhere",
                  tools: [
                    "Offline-capable mobile app",
                    "AR-assisted troubleshooting",
                    "Parts availability in real-time",
                    "Customer history at a glance"
                  ],
                  quote: "It's like having the entire office in my pocket, but without the hassle."
                },
                {
                  role: "Warehouse Teams",
                  icon: "📦",
                  title: "Inventory Made Simple",
                  tools: [
                    "Voice-guided picking",
                    "Smart routing for efficiency",
                    "Instant cycle counting",
                    "Predictive stock alerts"
                  ],
                  quote: "We're moving 30% more volume with the same team. The system just makes sense."
                }
              ].map((ops, i) => (
                <motion.div
                  key={ops.role}
                  className="bg-gradient-to-br from-emerald-950/20 to-teal-950/20 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-6"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, borderColor: "rgba(16, 185, 129, 0.3)" }}
                >
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{ops.icon}</div>
                    <h4 className="text-lg font-bold text-adaptive">{ops.role}</h4>
                    <p className="text-sm text-emerald-400">{ops.title}</p>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {ops.tools.map((tool, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-text-secondary text-adaptive">
                        <span className="text-emerald-400 mt-0.5">✓</span>
                        <span>{tool}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <blockquote className="text-sm italic text-adaptive border-t border-white/10 pt-4">
                    "{ops.quote}"
                  </blockquote>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* IT and Technical Teams */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix">
              For IT Teams: Finally, An Ally Not a Burden
            </h3>
            
            <div className="bg-gradient-to-br from-purple-950/20 to-pink-950/20 backdrop-blur-xl border border-purple-500/20 rounded-3xl p-12">
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h4 className="text-xl font-bold mb-6 text-adaptive flex items-center gap-3">
                    <span className="text-2xl">🛠</span>
                    For System Administrators
                  </h4>
                  <div className="space-y-4">
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-purple-300 mb-2">Zero Infrastructure Headaches</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        Cloud-native architecture means no servers to maintain. Automatic scaling, 
                        self-healing systems, and built-in disaster recovery. Sleep better at night.
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-purple-300 mb-2">Integration Without Tears</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        REST APIs, GraphQL, webhooks – pick your flavor. Pre-built connectors for 
                        common systems. And if something's missing? Our integration builder lets you 
                        create custom connectors without writing code.
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-purple-300 mb-2">Security That's Actually Manageable</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        Single sign-on, role-based access control, and audit logs that actually make 
                        sense. Compliance reports generated automatically. Security doesn't have to be 
                        a full-time job anymore.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xl font-bold mb-6 text-adaptive flex items-center gap-3">
                    <span className="text-2xl">💻</span>
                    For Developers
                  </h4>
                  <div className="space-y-4">
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-pink-300 mb-2">APIs You'll Actually Enjoy</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        Clean, consistent, well-documented APIs. SDKs in all major languages. 
                        Postman collections and OpenAPI specs included. Build on HighPoint without 
                        the usual enterprise software pain.
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-pink-300 mb-2">Extensibility Done Right</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        Create custom modules, workflows, and reports. Use our component library or 
                        bring your own. Deploy to production with CI/CD pipelines. Your code, your way.
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <h5 className="font-semibold text-pink-300 mb-2">Modern Tech Stack</h5>
                      <p className="text-sm text-text-secondary text-adaptive">
                        React, Node.js, GraphQL, Kubernetes – technologies you know and love. 
                        No legacy baggage, no proprietary languages. Just clean, modern architecture 
                        that makes sense.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
                <p className="text-center text-adaptive font-medium">
                  <span className="text-lg">🎉</span> Fun fact: Our support team's average response time is under 5 minutes. 
                  Why? Because HighPoint rarely needs support. When it does, we're here with actual engineers, not script readers.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Implementation Journey Section */}
      <section id="journey" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-6xl font-bold mb-6 bg-gradient-to-r from-rose-400 via-orange-400 to-amber-400 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              From Signup to Success in Minutes
            </h2>
            <p className="text-2xl text-white max-w-5xl mx-auto leading-relaxed mb-6">
              No consultants. No months-long implementations. No vendor dependencies. 
              Just sign up and start transforming your business immediately.
            </p>
            <p className="text-xl text-white max-w-4xl mx-auto">
              HighPoint learns your business patterns, adapts to your workflows, and evolves 
              with your growth – all automatically, all without writing a single line of code.
            </p>
          </motion.div>

          {/* Implementation Timeline */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent gradient-text-fix">
                How HighPoint Adapts to You
              </h3>
              <p className="text-xl text-white max-w-4xl mx-auto leading-relaxed">
                Watch how the platform intelligently learns your business and configures itself 
                automatically – no setup wizards, no configuration hell, no external help required.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  phase: "Instant Signup & Start",
                  duration: "2 Minutes",
                  icon: "⚡",
                  gradient: "from-yellow-400 to-orange-500",
                  description: "Self-service account creation with immediate platform access. No waiting, no approvals, no setup calls.",
                  features: ["One-click signup", "Instant environment", "Sample data included", "AI-guided tour"]
                },
                {
                  phase: "Intelligent Discovery",
                  duration: "First Week",
                  icon: "🧠",
                  gradient: "from-blue-400 to-purple-500",
                  description: "AI silently observes your business patterns and learns your workflows without any manual configuration.",
                  features: ["Pattern recognition", "Workflow learning", "Auto-integration discovery", "Smart optimization"]
                },
                {
                  phase: "Dynamic Self-Configuration",
                  duration: "Automatic",
                  icon: "🔧",
                  gradient: "from-emerald-400 to-teal-500",
                  description: "Platform configures itself based on learned patterns, generating workflows and adapting UI automatically.",
                  features: ["Auto-module setup", "Workflow generation", "UI adaptation", "System discovery"]
                },
                {
                  phase: "Self-Service Customization",
                  duration: "Anytime",
                  icon: "🎨",
                  gradient: "from-pink-400 to-rose-500",
                  description: "Every user customizes their experience through visual tools. No coding, no IT requests, no delays.",
                  features: ["Drag-drop editing", "Custom forms", "Personal dashboards", "Real-time changes"]
                },
                {
                  phase: "Continuous Evolution",
                  duration: "Always On",
                  icon: "🌱",
                  gradient: "from-green-400 to-emerald-500",
                  description: "Platform grows with your business, automatically adapting to changes and self-healing issues.",
                  features: ["Adaptive scaling", "Smart suggestions", "Auto-optimization", "Self-healing processes"]
                },
                {
                  phase: "Power User Freedom",
                  duration: "On Demand",
                  icon: "🚀",
                  gradient: "from-indigo-400 to-blue-500",
                  description: "Advanced users unleash unlimited customization through APIs and visual builders. No vendor limits.",
                  features: ["Custom integrations", "Advanced workflows", "Event handlers", "API freedom"]
                }
              ].map((phase, i) => (
                <motion.div
                  key={phase.phase}
                  className="relative h-full"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="relative bg-transparent rounded-2xl p-6 sm:p-8 h-full flex flex-col">
                    {/* Header with icon and timing */}
                    <div className="flex items-start justify-between mb-6">
                      <motion.div
                        className="text-5xl"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        {phase.icon}
                      </motion.div>
                      
                      <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${phase.gradient} text-black text-sm font-medium`}>
                        {phase.duration}
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h4 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${phase.gradient} bg-clip-text text-transparent gradient-text-fix`}>
                      {phase.phase}
                    </h4>
                    
                    {/* Description */}
                    <p className="text-white leading-relaxed mb-6 flex-grow">
                      {phase.description}
                    </p>
                    
                    {/* Feature bullets */}
                    <div className="space-y-3">
                      {phase.features.map((feature, j) => (
                        <motion.div
                          key={feature}
                          className="flex items-center gap-3"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.2 + i * 0.05 + j * 0.02 }}
                          viewport={{ once: true }}
                        >
                          <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${phase.gradient}`} />
                          <span className="text-sm text-white">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Implementation Benefits */}
          <motion.div 
            className="mt-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-4xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent gradient-text-fix">
                Agility That Rigid ERPs Can't Match
              </h3>
              <p className="text-xl text-white max-w-4xl mx-auto leading-relaxed">
                While legacy systems lock you into their way of doing things, HighPoint adapts 
                instantly to your changing business. No change requests, no development cycles, no vendor delays.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                capability: "Instant Adaptability",
                features: [
                  { label: "Workflow Changes", value: "Real-Time", color: "from-blue-500 to-cyan-400" },
                  { label: "Process Modification", value: "Self-Service", color: "from-purple-500 to-pink-400" },
                  { label: "Business Rules", value: "User-Defined", color: "from-emerald-500 to-teal-400" },
                  { label: "System Evolution", value: "Automatic", color: "from-orange-500 to-red-400" }
                ],
                icon: "⚡"
              },
              {
                capability: "Zero-Code Customization",
                features: [
                  { label: "UI Personalization", value: "Drag & Drop", color: "from-indigo-500 to-purple-400" },
                  { label: "Form Creation", value: "Visual Builder", color: "from-rose-500 to-pink-400" },
                  { label: "Report Design", value: "Self-Service", color: "from-cyan-500 to-blue-400" },
                  { label: "Integration Setup", value: "Point & Click", color: "from-amber-500 to-orange-400" }
                ],
                icon: "🎨"
              },
              {
                capability: "Intelligent Automation",
                features: [
                  { label: "Process Learning", value: "AI-Powered", color: "from-teal-500 to-emerald-400" },
                  { label: "Smart Suggestions", value: "Contextual", color: "from-violet-500 to-indigo-400" },
                  { label: "Auto-Optimization", value: "Continuous", color: "from-red-500 to-rose-400" },
                  { label: "Self-Healing", value: "Proactive", color: "from-blue-500 to-indigo-400" }
                ],
                icon: "🧠"
              }
            ].map((capability, i) => (
              <motion.div
                key={capability.capability}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="relative bg-transparent rounded-2xl p-6 h-full flex flex-col">
                  <div className="text-center mb-6 flex-shrink-0">
                    <motion.div 
                      className="text-4xl mb-3"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      {capability.icon}
                    </motion.div>
                    <h4 className="text-xl font-bold text-white">{capability.capability}</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {capability.features.map((feature, j) => (
                      <motion.div
                        key={feature.label}
                        className="flex justify-between items-center py-3 relative last:after:hidden"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: i * 0.05 + j * 0.02 }}
                        viewport={{ once: true }}
                        style={{
                          '--gradient': `linear-gradient(90deg, ${
                            feature.color === 'from-blue-500 to-cyan-400' ? '#3b82f6, #22d3ee' :
                            feature.color === 'from-purple-500 to-pink-400' ? '#a855f7, #f472b6' :
                            feature.color === 'from-emerald-500 to-teal-400' ? '#10b981, #2dd4bf' :
                            feature.color === 'from-orange-500 to-red-400' ? '#f97316, #f87171' :
                            feature.color === 'from-indigo-500 to-purple-400' ? '#6366f1, #c084fc' :
                            feature.color === 'from-rose-500 to-pink-400' ? '#f43f5e, #f472b6' :
                            feature.color === 'from-cyan-500 to-blue-400' ? '#06b6d4, #60a5fa' :
                            feature.color === 'from-amber-500 to-orange-400' ? '#f59e0b, #fb923c' :
                            feature.color === 'from-teal-500 to-emerald-400' ? '#14b8a6, #34d399' :
                            feature.color === 'from-violet-500 to-indigo-400' ? '#8b5cf6, #818cf8' :
                            feature.color === 'from-red-500 to-rose-400' ? '#ef4444, #fb7185' :
                            '#3b82f6, #6366f1'
                          })`
                        } as React.CSSProperties}
                      >
                        <span className="text-base text-white">{feature.label}</span>
                        <span className={`text-base font-semibold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent gradient-text-fix`}>
                          {feature.value}
                        </span>
                        <div 
                          className="absolute bottom-0 left-0 right-0 h-[1px]"
                          style={{
                            background: `linear-gradient(90deg, ${
                              feature.color === 'from-blue-500 to-cyan-400' ? '#3b82f6, #22d3ee' :
                              feature.color === 'from-purple-500 to-pink-400' ? '#a855f7, #f472b6' :
                              feature.color === 'from-emerald-500 to-teal-400' ? '#10b981, #2dd4bf' :
                              feature.color === 'from-orange-500 to-red-400' ? '#f97316, #f87171' :
                              feature.color === 'from-indigo-500 to-purple-400' ? '#6366f1, #c084fc' :
                              feature.color === 'from-rose-500 to-pink-400' ? '#f43f5e, #f472b6' :
                              feature.color === 'from-cyan-500 to-blue-400' ? '#06b6d4, #60a5fa' :
                              feature.color === 'from-amber-500 to-orange-400' ? '#f59e0b, #fb923c' :
                              feature.color === 'from-teal-500 to-emerald-400' ? '#14b8a6, #34d399' :
                              feature.color === 'from-violet-500 to-indigo-400' ? '#8b5cf6, #818cf8' :
                              feature.color === 'from-red-500 to-rose-400' ? '#ef4444, #fb7185' :
                              '#3b82f6, #6366f1'
                            })`
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* What is HighPoint Section */}
      <section id="platform" className="py-32 relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-indigo-400 to-purple-300 bg-clip-text text-transparent gradient-text-fix">
              The Central Nervous System of Your Enterprise
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive">
              HighPoint isn't another ERP trying to be everything to everyone. It's the orchestration 
              layer that makes your existing systems actually work together. Think of it as the conductor 
              that turns your IT chaos into a symphony.
            </p>
          </motion.div>

          {/* Department Connection Visualization */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative max-w-5xl mx-auto">
              {/* Central Hub */}
              <motion.div 
                className="relative z-20 mb-16"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-80 h-80 mx-auto relative">
                  {/* Outer glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-full blur-3xl animate-pulse" />
                  
                  {/* Multiple rotating rings - more condensed */}
                  <div className="absolute inset-0 border-2 border-blue-400/50 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
                  <div className="absolute inset-2 border border-purple-400/40 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }} />
                  <div className="absolute inset-4 border border-cyan-400/30 rounded-full animate-spin" style={{ animationDuration: '25s' }} />
                  
                  {/* Inner core with enhanced gradient - 20% larger */}
                  <div className="absolute inset-12 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20 rounded-full backdrop-blur-sm border border-white/20" />
                  
                  {/* Center content */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl font-bold bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent gradient-text-fix mb-2">HighPoint</div>
                      <div className="text-base text-white font-medium">Orchestration Hub</div>
                    </div>
                  </div>
                  
                </div>
              </motion.div>

              {/* Connected Departments Grid */}
              <div className="grid lg:grid-cols-3 gap-14">
                {[
                  { name: "Finance", desc: "Order-to-cash, procure-to-pay, financial reporting, budget management", icon: "💰", color: "from-green-500 to-emerald-400", features: ["GL Integration", "Multi-Currency", "Tax Engine", "Cost Centers"] },
                  { name: "Supply Chain", desc: "Inventory optimization, demand planning, warehouse management, logistics", icon: "📦", color: "from-blue-500 to-cyan-400", features: ["Real-time Tracking", "Route Optimization", "EDI Integration", "Forecasting"] },
                  { name: "Production", desc: "Shop floor control, quality management, maintenance, resource planning", icon: "🏭", color: "from-purple-500 to-pink-400", features: ["MES Integration", "OEE Tracking", "Quality Control", "Preventive Maintenance"] },
                  { name: "HR", desc: "Employee lifecycle, payroll integration, time tracking, compliance", icon: "👥", color: "from-orange-500 to-red-400", features: ["Onboarding", "Performance", "Time & Attendance", "Benefits Admin"] },
                  { name: "CRM", desc: "Customer 360°, sales automation, service management, marketing", icon: "🤝", color: "from-indigo-500 to-purple-400", features: ["Lead Management", "Opportunity Tracking", "Service Tickets", "Campaign Management"] },
                  { name: "Analytics", desc: "Real-time dashboards, predictive analytics, custom reporting, KPI monitoring", icon: "📊", color: "from-teal-500 to-cyan-400", features: ["Real-time BI", "Predictive Models", "Custom Reports", "Data Lake Access"] }
                ].map((dept, i) => (
                  <motion.div
                    key={dept.name}
                    className="relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * i }}
                    viewport={{ once: true }}
                  >
                    <div 
                      className="relative rounded-2xl p-[1px] h-full"
                      style={{
                        background: `linear-gradient(135deg, ${
                          dept.color === 'from-green-500 to-emerald-400' ? '#22c55e, #34d399' :
                          dept.color === 'from-blue-500 to-cyan-400' ? '#3b82f6, #22d3ee' :
                          dept.color === 'from-purple-500 to-pink-400' ? '#a855f7, #f472b6' :
                          dept.color === 'from-orange-500 to-red-400' ? '#f97316, #f87171' :
                          dept.color === 'from-indigo-500 to-purple-400' ? '#6366f1, #c084fc' :
                          '#14b8a6, #22d3ee'
                        })`
                      }}
                    >
                      <div className="relative bg-black backdrop-blur-xl rounded-2xl p-4 h-full transition-all duration-300 flex flex-col">
                      {/* Enhanced Connection Line to Hub */}
                      <motion.div 
                        className="absolute top-0 left-1/2 w-0.5 h-12 -translate-y-12 -translate-x-px"
                        style={{
                          background: `linear-gradient(to top, ${
                            dept.color === 'from-green-500 to-emerald-400' ? '#22c55e' :
                            dept.color === 'from-blue-500 to-cyan-400' ? '#3b82f6' :
                            dept.color === 'from-purple-500 to-pink-400' ? '#a855f7' :
                            dept.color === 'from-orange-500 to-red-400' ? '#f97316' :
                            dept.color === 'from-indigo-500 to-purple-400' ? '#6366f1' :
                            '#14b8a6'
                          }, transparent)`,
                          boxShadow: `0 0 8px ${
                            dept.color === 'from-green-500 to-emerald-400' ? '#22c55e' :
                            dept.color === 'from-blue-500 to-cyan-400' ? '#3b82f6' :
                            dept.color === 'from-purple-500 to-pink-400' ? '#a855f7' :
                            dept.color === 'from-orange-500 to-red-400' ? '#f97316' :
                            dept.color === 'from-indigo-500 to-purple-400' ? '#6366f1' :
                            '#14b8a6'
                          }40`
                        }}
                        initial={{ height: 0, opacity: 0 }}
                        whileInView={{ height: 48, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                        viewport={{ once: true }}
                      />
                      
                      <div className="flex items-start gap-4 mb-4">
                        <motion.div 
                          className="text-4xl"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          {dept.icon}
                        </motion.div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold mb-2" style={{
                            background: `linear-gradient(135deg, ${
                              dept.color === 'from-green-500 to-emerald-400' ? '#22c55e, #34d399' :
                              dept.color === 'from-blue-500 to-cyan-400' ? '#3b82f6, #22d3ee' :
                              dept.color === 'from-purple-500 to-pink-400' ? '#a855f7, #f472b6' :
                              dept.color === 'from-orange-500 to-red-400' ? '#f97316, #f87171' :
                              dept.color === 'from-indigo-500 to-purple-400' ? '#6366f1, #c084fc' :
                              '#14b8a6, #22d3ee'
                            })`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}>
                            {dept.name}
                          </h3>
                          <p className="text-sm text-white font-medium leading-relaxed">
                            {dept.desc}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 mt-2 flex-1">
                        {dept.features.map((feature, j) => (
                          <motion.div 
                            key={j}
                            className="text-sm text-white font-semibold flex items-center gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 + i * 0.1 + j * 0.05 }}
                            viewport={{ once: true }}
                          >
                            <div 
                              className="w-2 h-2 rounded-full border-2 border-white/20 flex-shrink-0"
                              style={{
                                background: `linear-gradient(135deg, ${
                                  dept.color === 'from-green-500 to-emerald-400' ? '#4ade80, #6ee7b7' :
                                  dept.color === 'from-blue-500 to-cyan-400' ? '#60a5fa, #67e8f9' :
                                  dept.color === 'from-purple-500 to-pink-400' ? '#c084fc, #f9a8d4' :
                                  dept.color === 'from-orange-500 to-red-400' ? '#fb923c, #fca5a5' :
                                  dept.color === 'from-indigo-500 to-purple-400' ? '#818cf8, #d8b4fe' :
                                  '#5eead4, #7dd3fc'
                                })`,
                                boxShadow: `0 0 4px ${
                                  dept.color === 'from-green-500 to-emerald-400' ? '#4ade80' :
                                  dept.color === 'from-blue-500 to-cyan-400' ? '#60a5fa' :
                                  dept.color === 'from-purple-500 to-pink-400' ? '#c084fc' :
                                  dept.color === 'from-orange-500 to-red-400' ? '#fb923c' :
                                  dept.color === 'from-indigo-500 to-purple-400' ? '#818cf8' :
                                  '#5eead4'
                                }40`
                              }}
                            />
                            <span className="leading-tight">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Key Differentiators */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-32">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="w-24 h-24 mx-auto mb-6 relative"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-cyan-400/30 rounded-2xl blur-2xl" />
                <div 
                  className="relative rounded-2xl p-[2px] h-full"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6, #06b6d4)'
                  }}
                >
                  <div className="bg-black rounded-2xl flex items-center justify-center h-full backdrop-blur-xl">
                    <span className="text-4xl">🧠</span>
                  </div>
                </div>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix">
                Actually Intelligent
              </h3>
              <p className="text-text-secondary text-adaptive leading-relaxed">
                Not just "AI-powered" marketing fluff. HighPoint will learn your workflows, 
                predict bottlenecks, and fix issues before your coffee gets cold.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="w-24 h-24 mx-auto mb-6 relative"
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-400/20 rounded-2xl blur-2xl" />
                <div 
                  className="relative rounded-2xl p-[2px] h-full"
                  style={{
                    background: 'linear-gradient(135deg, #a855f7, #f472b6)'
                  }}
                >
                  <div className="bg-black rounded-2xl flex items-center justify-center h-full backdrop-blur-xl">
                    <span className="text-4xl">🔓</span>
                  </div>
                </div>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix">
                No Vendor Prison
              </h3>
              <p className="text-text-secondary text-adaptive leading-relaxed">
                Export your data anytime. Use any tool you want. We're confident you'll 
                stay because HighPoint will work, not because we trapped you.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="w-24 h-24 mx-auto mb-6 relative"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-teal-400/30 rounded-2xl blur-2xl" />
                <div 
                  className="relative rounded-2xl p-[2px] h-full"
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #14b8a6)'
                  }}
                >
                  <div className="bg-black rounded-2xl flex items-center justify-center h-full backdrop-blur-xl">
                    <span className="text-4xl">⚡</span>
                  </div>
                </div>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
                Built for Speed
              </h3>
              <p className="text-text-secondary text-adaptive leading-relaxed">
                Deploy in weeks, not years. See results in days, not quarters. 
                We're stripping out the consultant-required complexity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Functions - Comprehensive Section */}
      <LazySection>
        <section id="business-functions" className="py-32 relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              Seven Platforms in One
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-white max-w-5xl mx-auto leading-relaxed mb-6">
              Stop managing seven different vendors, seven different contracts, and seven different sets of bugs. 
              HighPoint will replace your entire business software stack with one integrated platform.
            </p>
            <p className="text-base sm:text-lg lg:text-xl text-white max-w-4xl mx-auto">
              Each module will be purpose-built for its function, yet seamlessly integrated with all others. 
              No data silos. No integration headaches. No compromises.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
            {[
              {
                title: "ERP",
                subtitle: "Enterprise Resource Planning",
                description: "Complete financial and operational management system that will actually make sense. Being built for modern businesses, not 1990s accounting departments.",
                icon: "🏭",
                color: "from-blue-500 to-cyan-500",
                features: [
                  "General Ledger & Financial Reporting",
                  "Accounts Payable & Receivable", 
                  "Purchase Order Management",
                  "Inventory Control & Valuation",
                  "Order Management & Fulfillment",
                  "Multi-Currency & Multi-Entity"
                ],
                highlight: "Will replace SAP, Oracle, NetSuite at 75% less cost"
              },
              {
                title: "MES",
                subtitle: "Manufacturing Execution System", 
                description: "Real-time production control and visibility. From work instructions to quality data collection, you'll manage your entire shop floor digitally.",
                icon: "⚙️",
                color: "from-orange-500 to-red-500",
                features: [
                  "Work Order Management",
                  "Digital Work Instructions",
                  "Real-Time Data Collection",
                  "OEE & Performance Analytics",
                  "Genealogy & Traceability",
                  "Machine Integration & IoT"
                ],
                highlight: "Will connect directly to PLCs, SCADA, and existing machinery"
              },
              {
                title: "QMS", 
                subtitle: "Quality Management System",
                description: "FDA 21 CFR Part 11 compliant quality management. From document control to CAPA management, you'll maintain compliance without the paperwork nightmare.",
                icon: "🔍",
                color: "from-emerald-500 to-teal-500",
                features: [
                  "Document Control & Versioning",
                  "Non-Conformance Management",
                  "CAPA (Corrective & Preventive Action)",
                  "Audit Management & Trails",
                  "Supplier Qualification",
                  "Training & Competency Tracking"
                ],
                highlight: "Audit-ready compliance with automated workflows"
              },
              {
                title: "CRM",
                subtitle: "Customer Relationship Management",
                description: "Sales, marketing, and service management that won't require a PhD in Salesforce administration. Focus on customers, not configuration.",
                icon: "🤝",
                color: "from-purple-500 to-indigo-500",
                features: [
                  "Lead & Opportunity Management",
                  "Customer 360° View",
                  "Sales Pipeline & Forecasting",
                  "Marketing Automation",
                  "Service Ticket Management",
                  "Customer Portal & Self-Service"
                ],
                highlight: "Native integration with sales, service, and billing processes"
              },
              {
                title: "FSM",
                subtitle: "Field Service Management",
                description: "Intelligent dispatch, route optimization, and mobile workforce management. Get the right technician to the right place with the right parts.",
                icon: "🚚", 
                color: "from-cyan-500 to-blue-500",
                features: [
                  "Intelligent Scheduling & Dispatch",
                  "Route Optimization & GPS Tracking",
                  "Skill-Based Resource Matching", 
                  "Mobile Work Order Management",
                  "Parts & Inventory Sync",
                  "Customer Communication & Portals"
                ],
                highlight: "Reduces travel time by 30% with AI-powered optimization"
              },
              {
                title: "CMMS",
                subtitle: "Computerized Maintenance Management",
                description: "Predictive and preventive maintenance that keeps your assets running. From work orders to spare parts, manage maintenance intelligently.",
                icon: "🔧",
                color: "from-teal-500 to-emerald-500",
                features: [
                  "Asset Management & Tracking",
                  "Preventive Maintenance Scheduling",
                  "Predictive Analytics & IoT Integration",
                  "Work Order Management",
                  "Spare Parts Inventory",
                  "Maintenance KPIs & Reporting"
                ],
                highlight: "AI predicts failures before they happen"
              },
              {
                title: "PSA",
                subtitle: "Professional Services Automation",
                description: "Project management, time tracking, and billing for service organizations. From project initiation to final invoice, manage the entire service lifecycle.",
                icon: "📊",
                color: "from-indigo-500 to-purple-500",
                features: [
                  "Project Planning & Management",
                  "Resource Allocation & Scheduling",
                  "Time Tracking & Expense Management",
                  "Project Billing & Invoicing",
                  "Client Collaboration & Portals",
                  "Profitability Analytics"
                ],
                highlight: "Integrated with HaloPSA for existing users"
              }
            ].slice(0, showSimplifiedContent ? 3 : undefined).map((module, i) => (
              <motion.div
                key={module.title}
                className="relative group h-full"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${module.color} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-300 blur-xl`} />
                
                <div 
                  className="relative rounded-3xl p-[1px] h-full"
                  style={{
                    background: `linear-gradient(135deg, ${module.color.includes('blue-500 to-cyan-500') ? '#3b82f6, #06b6d4' : 
                      module.color.includes('orange-500 to-red-500') ? '#f97316, #ef4444' :
                      module.color.includes('emerald-500 to-teal-500') ? '#10b981, #14b8a6' :
                      module.color.includes('purple-500 to-indigo-500') ? '#8b5cf6, #6366f1' :
                      module.color.includes('cyan-500 to-blue-500') ? '#06b6d4, #3b82f6' :
                      module.color.includes('teal-500 to-emerald-500') ? '#14b8a6, #10b981' :
                      module.color.includes('indigo-500 to-purple-500') ? '#6366f1, #8b5cf6' : '#3b82f6, #06b6d4'})`
                  }}
                >
                  <div className="relative bg-black backdrop-blur-xl rounded-3xl p-6 sm:p-8 h-full flex flex-col">
                  <div className="flex items-start gap-4 mb-6">
                    <motion.div 
                      className="text-4xl opacity-90"
                      animate={{ y: [0, -5, 0], rotate: [0, 2, -2, 0] }}
                      transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}
                    >
                      {module.icon}
                    </motion.div>
                    <div className="flex-1">
                      <h3 className={`text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r ${module.color} bg-clip-text text-transparent gradient-text-fix`}>
                        {module.title}
                      </h3>
                      <h4 className="text-base sm:text-lg font-medium text-white mb-4">
                        {module.subtitle}
                      </h4>
                    </div>
                  </div>
                  
                  <div className="mb-6 flex-grow">
                    <p className="text-white leading-relaxed mb-4">
                      {module.description}
                    </p>
                    
                    <div className="space-y-2">
                      {module.features.map((feature, j) => (
                        <motion.div
                          key={feature}
                          className="flex items-center gap-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + i * 0.1 + j * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <motion.div 
                            className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${module.color}`}
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ duration: 2, repeat: Infinity, delay: j * 0.2 }}
                          />
                          <span className="text-white text-xs sm:text-sm">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                  <div className={`bg-gradient-to-r ${module.color} p-0.5 rounded-xl mt-auto`}>
                    <div className="bg-black/80 rounded-xl p-3">
                      <p className="text-white text-xs sm:text-sm font-medium text-center">
                        💡 {module.highlight}
                      </p>
                    </div>
                  </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Show more on mobile */}
          {showSimplifiedContent && (
            <div className="text-center mb-12">
              <p className="text-white/60 text-sm">
                + 11 more platforms available on desktop view
              </p>
            </div>
          )}

          {/* Integration Benefits */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-4xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent gradient-text-fix">
                The Power of True Integration
              </h3>
              <p className="text-xl text-white max-w-4xl mx-auto leading-relaxed">
                When your business functions share the same data model, use the same workflow engine, 
                and speak the same language, magic happens.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  title: "Single Source of Truth",
                  description: "Customer data, inventory levels, financial records - everything syncs instantly across all modules. No duplicate entry, no reconciliation headaches.",
                  icon: "🎯",
                  color: "from-blue-500 to-cyan-500",
                  examples: ["Customer order updates inventory in real-time", "Service tickets automatically create billing entries", "Quality issues trigger supplier notifications"]
                },
                {
                  title: "Cross-Function Workflows", 
                  description: "Create business processes that span multiple functions seamlessly. From quote to cash, hire to retire - orchestrate complex operations simply.",
                  icon: "🔄",
                  color: "from-emerald-500 to-teal-500",
                  examples: ["Sales order triggers production and procurement", "Equipment maintenance creates service opportunities", "Quality failures update supplier scorecards"]
                },
                {
                  title: "Unified Analytics",
                  description: "See the complete picture with analytics that span your entire operation. No more siloed reports that don't tell the whole story.",
                  icon: "📊",
                  color: "from-purple-500 to-indigo-500",
                  examples: ["Customer profitability including service costs", "True equipment ROI with maintenance expenses", "End-to-end process efficiency metrics"]
                }
              ].map((benefit, i) => (
                <motion.div
                  key={benefit.title}
                  className="relative group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${benefit.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300 blur-xl`} />
                  
                  <div className="relative bg-transparent rounded-2xl p-6 sm:p-8 h-full flex flex-col">
                    <motion.div 
                      className="text-4xl sm:text-5xl mb-6 flex-shrink-0 text-center"
                      animate={{ scale: [1, 1.02, 1] }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                    >
                      {benefit.icon}
                    </motion.div>
                    
                    <h4 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${benefit.color} bg-clip-text text-transparent gradient-text-fix`}>
                      {benefit.title}
                    </h4>
                    
                    <div className="flex-grow">
                      <p className="text-white mb-6 leading-relaxed">
                        {benefit.description}
                      </p>
                      
                      <div className="space-y-3">
                        {benefit.examples.map((example, j) => (
                          <motion.div
                            key={example}
                            className="flex items-start gap-3"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 + i * 0.2 + j * 0.1 }}
                            viewport={{ once: true }}
                          >
                            <motion.div 
                              className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${benefit.color} mt-2 flex-shrink-0`}
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 4, repeat: Infinity, delay: j * 0.5 }}
                            />
                            <span className="text-white text-xs sm:text-sm leading-relaxed">{example}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-emerald-900/50 via-teal-900/50 to-cyan-900/50 backdrop-blur-xl border border-white/10 rounded-3xl p-12">
              <motion.h3 
                className="text-4xl font-bold mb-6 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent gradient-text-fix"
                animate={{ backgroundPosition: ["0%", "100%", "0%"] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                Stop Managing Seven Vendors
              </motion.h3>
              
              <p className="text-2xl text-white mb-8 max-w-4xl mx-auto leading-relaxed">
                One platform. One contract. One throat to choke. One monthly bill. 
                And most importantly - one cohesive system that actually works.
              </p>
              
              <p className="text-xl text-white mb-10 max-w-3xl mx-auto">
                See how HighPoint replaces your entire business software stack with a single, 
                integrated platform that costs less than what you're paying for just one of these systems today.
              </p>
              
              <motion.button
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-12 py-4 rounded-xl font-bold text-lg shadow-lg shadow-emerald-500/25 transition-all duration-300"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.4)" }}
                whileTap={{ scale: 0.95 }}
              >
                See Platform Integration
              </motion.button>
            </div>
          </motion.div>
        </div>
        </section>
      </LazySection>

      {/* Architecture Deep Dive Section */}
      <section id="architecture" className="py-32 relative bg-gradient-to-b from-transparent via-blue-950/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent gradient-text-fix">
              Architecture That Will Actually Make Sense
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive mb-4">
              Forget the 47-layer enterprise architecture diagrams that require a PhD to understand. 
              HighPoint will use a clean three-layer approach that your team can actually work with.
            </p>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto text-adaptive opacity-90">
              (Yes, it will really be just three layers. We're not hiding 12 more behind consulting fees.)
            </p>
          </motion.div>
          
          {/* Architecture Capabilities */}
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {[
              { label: "Database Types", value: "5", desc: "Multi-model approach", color: "from-green-500 to-emerald-400" },
              { label: "API Latency", value: "<50ms", desc: "P95 response time", color: "from-blue-500 to-cyan-400" },
              { label: "Event Throughput", value: "100K/s", desc: "Messages processed", color: "from-purple-500 to-pink-400" },
              { label: "Data Retention", value: "Custom", desc: "Compliance ready", color: "from-orange-500 to-red-400" }
            ].map((metric, i) => (
              <motion.div 
                key={metric.label}
                className="relative overflow-hidden group"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div 
                  className="rounded-2xl p-[1px] h-full"
                  style={{
                    background: `linear-gradient(135deg, ${
                      metric.color.includes('green-500 to-emerald-400') ? '#22c55e, #10b981' :
                      metric.color.includes('blue-500 to-cyan-400') ? '#3b82f6, #06b6d4' :
                      metric.color.includes('purple-500 to-pink-400') ? '#8b5cf6, #f472b6' :
                      metric.color.includes('orange-500 to-red-400') ? '#f97316, #f87171' : '#3b82f6, #06b6d4'
                    })`
                  }}
                >
                  <div className="bg-black backdrop-blur-xl rounded-2xl p-6 relative overflow-hidden h-full">
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-r ${metric.color} opacity-0 group-hover:opacity-5`}
                  transition={{ duration: 0.3 }}
                />
                <div className="relative z-10">
                  <div className="text-sm text-adaptive opacity-70 mb-1">{metric.label}</div>
                  <div className={`text-3xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent gradient-text-fix mb-1`}>
                    {metric.value}
                  </div>
                  <div className="text-xs text-adaptive opacity-70">
                    {metric.desc}
                  </div>
                </div>
                </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Three-Layer Architecture Visualization */}
          <div className="space-y-12">
            {/* Layer 1: Presentation */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="lg:w-1/3">
                  <div className="text-sm text-cyan-400 mb-2">Layer 1</div>
                  <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent gradient-text-fix">
                    Presentation Layer
                  </h3>
                  <p className="text-text-secondary text-adaptive mb-4">
                    The face of HighPoint. Beautiful, responsive interfaces that your team will 
                    actually want to use. No more "enterprise ugly" – this will be UI that sparks joy.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-cyan-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <span className="text-sm text-adaptive">Modern React 18 + TypeScript</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-blue-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                      />
                      <span className="text-sm text-adaptive">Real-time WebSocket updates</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-indigo-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
                      />
                      <span className="text-sm text-adaptive">Responsive & accessible</span>
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-2/3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {["Dashboard", "Analytics", "Workflows", "Reports", "Settings", "Mobile"].map((item, i) => (
                      <motion.div
                        key={item}
                        className="bg-gradient-to-br from-cyan-950/30 to-blue-950/30 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-4 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        whileHover={{ 
                          scale: 1.05,
                          borderColor: "rgba(6, 182, 212, 0.4)",
                          backgroundColor: "rgba(6, 182, 212, 0.05)"
                        }}
                      >
                        <div className="text-2xl mb-2">{
                          item === "Dashboard" ? "📈" :
                          item === "Analytics" ? "📊" :
                          item === "Workflows" ? "⚙️" :
                          item === "Reports" ? "📄" :
                          item === "Settings" ? "🔧" :
                          "📱"
                        }</div>
                        <div className="text-sm text-adaptive">{item}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Connection Lines */}
            <div className="flex justify-center">
              <motion.div 
                className="w-1 h-16 bg-gradient-to-b from-cyan-500/50 to-purple-500/50"
                initial={{ height: 0 }}
                whileInView={{ height: 64 }}
                transition={{ duration: 0.8 }}
              />
            </div>

            {/* Layer 2: Business Logic */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
                <div className="lg:w-1/3">
                  <div className="text-sm text-purple-400 mb-2">Layer 2</div>
                  <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix">
                    Business Logic Layer
                  </h3>
                  <p className="text-text-secondary text-adaptive mb-4">
                    The brain of the operation. This is where your business rules live, workflows 
                    orchestrate, and the magic happens. Completely customizable without touching code.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-purple-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <span className="text-sm text-adaptive">Node.js + GraphQL APIs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-pink-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                      />
                      <span className="text-sm text-adaptive">Temporal workflow engine</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-rose-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
                      />
                      <span className="text-sm text-adaptive">Redis for blazing speed</span>
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-2/3">
                  <div className="bg-gradient-to-br from-purple-950/30 to-pink-950/30 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6">
                    <h4 className="text-xl font-semibold text-adaptive mb-6 text-center">Core Business Services</h4>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* ERP Services */}
                      <div className="space-y-2">
                        <h5 className="text-sm font-semibold text-purple-300 mb-2">ERP Services</h5>
                        {["Order Management", "Financial Mgmt", "Procurement", "Master Data"].map((item, i) => (
                          <motion.div 
                            key={item}
                            className="flex items-center gap-2"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            viewport={{ once: true }}
                          >
                            <div className="w-1 h-1 bg-purple-400 rounded-full" />
                            <span className="text-xs text-adaptive opacity-80">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Manufacturing Services */}
                      <div className="space-y-2">
                        <h5 className="text-sm font-semibold text-pink-300 mb-2">Manufacturing</h5>
                        {["Production Control", "Quality Service", "Inventory Service", "Machine Integration"].map((item, i) => (
                          <motion.div 
                            key={item}
                            className="flex items-center gap-2"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            viewport={{ once: true }}
                          >
                            <div className="w-1 h-1 bg-pink-400 rounded-full" />
                            <span className="text-xs text-adaptive opacity-80">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Field Service */}
                      <div className="space-y-2">
                        <h5 className="text-sm font-semibold text-rose-300 mb-2">Field Service</h5>
                        {["Dispatch Service", "Route Optimization", "Mobile Service", "Contract Mgmt"].map((item, i) => (
                          <motion.div 
                            key={item}
                            className="flex items-center gap-2"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            viewport={{ once: true }}
                          >
                            <div className="w-1 h-1 bg-rose-400 rounded-full" />
                            <span className="text-xs text-adaptive opacity-80">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Temporal Workflows - Enhanced */}
                    <div className="mt-6 pt-6 border-t border-purple-500/20">
                      <h5 className="text-sm font-semibold text-purple-300 mb-3">Temporal Workflow Orchestration</h5>
                      
                      {/* Business Workflows */}
                      <div className="mb-4">
                        <h6 className="text-xs font-medium text-purple-200 mb-2">Core Business Workflows</h6>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { name: "Quote to Cash", icon: "💰", steps: "Entry → Validation → Production → Invoice → Collection" },
                            { name: "Procure to Pay", icon: "📦", steps: "Request → Approval → Receipt → Verification → Payment" },
                            { name: "Hire to Retire", icon: "👥", steps: "Onboard → Train → Manage → Review → Offboard" },
                            { name: "Order to Delivery", icon: "🚚", steps: "Order → Schedule → Pick → Pack → Ship → Confirm" }
                          ].map((workflow, i) => (
                            <motion.div 
                              key={workflow.name}
                              className="bg-purple-950/20 rounded-lg p-2 border border-purple-500/10"
                              initial={{ opacity: 0, scale: 0.9 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              transition={{ delay: i * 0.05 }}
                              viewport={{ once: true }}
                              whileHover={{ borderColor: "rgba(168, 85, 247, 0.3)" }}
                            >
                              <div className="flex items-center gap-2 text-xs font-medium text-white mb-1">
                                <span>{workflow.icon}</span>
                                <span>{workflow.name}</span>
                              </div>
                              <div className="text-xs text-purple-300/70 leading-tight">{workflow.steps}</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Industry Workflows */}
                      <div>
                        <h6 className="text-xs font-medium text-pink-200 mb-2">Industry-Specific Workflows</h6>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <div className="text-pink-300 font-medium mb-1">Manufacturing</div>
                            <div className="space-y-1">
                              <div className="text-white">• Production Order</div>
                              <div className="text-white">• Quality Control</div>
                              <div className="text-white">• Equipment Maint.</div>
                            </div>
                          </div>
                          <div>
                            <div className="text-pink-300 font-medium mb-1">Field Service</div>
                            <div className="space-y-1">
                              <div className="text-white">• Service Request</div>
                              <div className="text-white">• Dispatch Process</div>
                              <div className="text-white">• Billing Cycle</div>
                            </div>
                          </div>
                          <div>
                            <div className="text-pink-300 font-medium mb-1">IT Service</div>
                            <div className="space-y-1">
                              <div className="text-white">• Incident Response</div>
                              <div className="text-white">• Change Mgmt</div>
                              <div className="text-white">• Patch Deploy</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Connection Lines */}
            <div className="flex justify-center">
              <motion.div 
                className="w-1 h-16 bg-gradient-to-b from-purple-500/50 to-emerald-500/50"
                initial={{ height: 0 }}
                whileInView={{ height: 64 }}
                transition={{ duration: 0.8 }}
              />
            </div>

            {/* Layer 3: Data Infrastructure */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="lg:w-1/3">
                  <div className="text-sm text-emerald-400 mb-2">Layer 3</div>
                  <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
                    Data Infrastructure
                  </h3>
                  <p className="text-text-secondary text-adaptive mb-4">
                    Your single source of truth. Multi-database architecture that scales infinitely, 
                    with real-time sync and intelligent caching. Your data, your way.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-emerald-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <span className="text-sm text-adaptive">Neo4j Graph + Azure SQL</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-teal-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                      />
                      <span className="text-sm text-adaptive">Event sourcing with ADX</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="w-2 h-2 bg-cyan-400 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
                      />
                      <span className="text-sm text-adaptive">CQRS architecture</span>
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-2/3">
                  <div className="space-y-6">
                    {/* Primary Databases */}
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-300 mb-3">Multi-Model Database Architecture</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                          { name: "Neo4j Graph", desc: "Business entities & ISA-95", icon: "🕸", detail: "Universal business graph" },
                          { name: "Azure SQL", desc: "Transactional data", icon: "🗄", detail: "Serverless scaling" },
                          { name: "Cosmos DB", desc: "Configuration & metadata", icon: "🌐", detail: "Global distribution" },
                          { name: "Azure Data Explorer", desc: "Time-series & telemetry", icon: "📈", detail: "100K events/sec" },
                          { name: "Redis Cache", desc: "Session & hot data", icon: "⚡", detail: "<10ms latency" },
                          { name: "Blob Storage", desc: "Documents & media", icon: "📦", detail: "Unlimited scale" }
                        ].map((db, i) => (
                          <motion.div
                            key={db.name}
                            className="bg-gradient-to-br from-emerald-950/30 to-teal-950/30 backdrop-blur-xl border border-emerald-500/20 rounded-xl p-3"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                            viewport={{ once: true }}
                            whileHover={{ 
                              scale: 1.02,
                              borderColor: "rgba(16, 185, 129, 0.4)"
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="text-2xl">{db.icon}</div>
                              <div className="flex-1">
                                <div className="text-xs font-semibold text-adaptive">{db.name}</div>
                                <div className="text-xs text-white">{db.desc}</div>
                                <div className="text-xs text-emerald-400 mt-1">{db.detail}</div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Universal Business Graph */}
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-emerald-300 mb-3">Universal Business Graph (ISA-95 Compliant)</h4>
                      <div className="bg-gradient-to-br from-emerald-950/30 to-teal-950/30 backdrop-blur-xl border border-emerald-500/20 rounded-xl p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-xs font-medium text-emerald-200 mb-2">Core Entities</h5>
                            <div className="space-y-1">
                              {["Enterprise", "Site", "Area", "Work Center", "Process Cell", "Equipment"].map((entity) => (
                                <div key={entity} className="text-xs text-white flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                  {entity}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="text-xs font-medium text-teal-200 mb-2">Universal Relationships</h5>
                            <div className="space-y-1">
                              {["OWNS", "OPERATES", "LOCATED_AT", "PART_OF", "PRODUCES", "CONSUMES"].map((rel) => (
                                <div key={rel} className="text-xs text-white flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 bg-teal-400 rounded-full" />
                                  {rel}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-emerald-500/20">
                          <p className="text-xs text-emerald-300">
                            Industry extensions for Manufacturing, Field Service, IT Services, and Realty - all connected in one graph
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Data Features */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-black/30 backdrop-blur-xl border border-emerald-500/10 rounded-xl p-4">
                        <h5 className="text-sm font-semibold text-emerald-300 mb-2">Data Isolation</h5>
                        <div className="space-y-1">
                          <div className="text-xs text-white">• Database per tenant</div>
                          <div className="text-xs text-white">• Encryption at rest</div>
                          <div className="text-xs text-white">• Customer-managed keys</div>
                        </div>
                      </div>
                      <div className="bg-black/30 backdrop-blur-xl border border-emerald-500/10 rounded-xl p-4">
                        <h5 className="text-sm font-semibold text-emerald-300 mb-2">Event Sourcing</h5>
                        <div className="space-y-1">
                          <div className="text-xs text-white">• Complete audit trail</div>
                          <div className="text-xs text-white">• Time-travel queries</div>
                          <div className="text-xs text-white">• CQRS pattern</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Event Sourcing & CQRS Architecture */}
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-emerald-300 mb-3">Event-Driven Architecture with CQRS</h4>
                      <div className="bg-gradient-to-br from-emerald-950/30 to-teal-950/30 backdrop-blur-xl border border-emerald-500/20 rounded-xl p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-black/30 rounded-lg p-3">
                            <h5 className="text-xs font-medium text-cyan-300 mb-2">Command Side (Write)</h5>
                            <div className="space-y-1.5">
                              <div className="text-xs text-white">→ Command Handlers</div>
                              <div className="text-xs text-white">→ Domain Aggregates</div>
                              <div className="text-xs text-white">→ Event Store (ADX)</div>
                              <div className="text-xs text-emerald-400 font-medium">100K events/sec throughput</div>
                            </div>
                          </div>
                          <div className="bg-black/30 rounded-lg p-3">
                            <h5 className="text-xs font-medium text-teal-300 mb-2">Query Side (Read)</h5>
                            <div className="space-y-1.5">
                              <div className="text-xs text-white">← Read Models</div>
                              <div className="text-xs text-white">← Materialized Views</div>
                              <div className="text-xs text-white">← Query Handlers</div>
                              <div className="text-xs text-emerald-400 font-medium">&lt;50ms response time</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-emerald-500/20 text-center">
                          <p className="text-xs text-emerald-300">
                            Complete event history • Temporal consistency • Scalable reads/writes
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Multi-Tenant Architecture - Enhanced Data Controls */}
          <motion.div 
            className="mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent gradient-text-fix">
              Multi-Tenant Architecture Tiers
            </h3>
            <div className="grid lg:grid-cols-3 gap-6">
              {[
                {
                  tier: "Standard Tier",
                  features: [
                    { label: "Infrastructure", value: "Shared", color: "from-blue-500 to-cyan-400" },
                    { label: "Data Isolation", value: "Logical", color: "from-cyan-500 to-teal-400" },
                    { label: "Encryption", value: "Platform Keys", color: "from-teal-500 to-emerald-400" },
                    { label: "Performance", value: "Auto-Scaling", color: "from-emerald-500 to-green-400" }
                  ],
                  icon: "⚡",
                  highlight: "Best Value",
                  mainColor: "from-blue-500 to-cyan-400"
                },
                {
                  tier: "Enterprise Tier",
                  features: [
                    { label: "Infrastructure", value: "Dedicated", color: "from-purple-500 to-pink-400" },
                    { label: "Data Isolation", value: "Physical", color: "from-pink-500 to-rose-400" },
                    { label: "Encryption", value: "Customer-Managed", color: "from-rose-500 to-red-400" },
                    { label: "Performance", value: "Reserved", color: "from-red-500 to-orange-400" }
                  ],
                  icon: "🏢",
                  highlight: "Most Popular",
                  mainColor: "from-purple-500 to-pink-400"
                },
                {
                  tier: "High Security Tier",
                  features: [
                    { label: "Infrastructure", value: "Isolated", color: "from-indigo-500 to-purple-400" },
                    { label: "Data Isolation", value: "Air-Gapped", color: "from-purple-500 to-violet-400" },
                    { label: "Encryption", value: "HSM + CMK", color: "from-violet-500 to-indigo-400" },
                    { label: "Performance", value: "Guaranteed", color: "from-indigo-500 to-blue-400" }
                  ],
                  icon: "🔐",
                  highlight: "Gov Cloud",
                  mainColor: "from-indigo-500 to-purple-400"
                }
              ].map((tier, i) => (
                <motion.div
                  key={tier.tier}
                  className="relative group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${tier.mainColor} opacity-0 group-hover:opacity-20 rounded-2xl blur-xl transition-opacity duration-500`} />
                  
                  <div className="relative bg-black/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full">
                    <div className="text-center mb-6">
                      <motion.div 
                        className="text-5xl mb-3"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        {tier.icon}
                      </motion.div>
                      <h4 className="text-xl font-bold text-adaptive mb-2">{tier.tier}</h4>
                      <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r ${tier.mainColor} bg-opacity-20 text-adaptive`}>
                        {tier.highlight}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {tier.features.map((feature, j) => (
                        <motion.div
                          key={feature.label}
                          className="relative overflow-hidden rounded-lg"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 + j * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-10`} />
                          <div className="relative px-4 py-2 flex justify-between items-center">
                            <span className="text-sm text-white">{feature.label}</span>
                            <span className={`text-sm font-semibold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent gradient-text-fix`}>
                              {feature.value}
                            </span>
                          </div>
                          <motion.div
                            className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${feature.color}`}
                            initial={{ width: "0%" }}
                            whileInView={{ width: "100%" }}
                            transition={{ duration: 1, delay: i * 0.1 + j * 0.1 }}
                            viewport={{ once: true }}
                          />
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Technical Details */}
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="space-y-2">
                        {tier.tier === "Standard Tier" && (
                          <>
                            <div className="text-xs text-white">• Serverless SQL Database</div>
                            <div className="text-xs text-white">• Unique encryption key per tenant</div>
                            <div className="text-xs text-white">• Logical data isolation</div>
                          </>
                        )}
                        {tier.tier === "Enterprise Tier" && (
                          <>
                            <div className="text-xs text-white">• Dedicated compute resources</div>
                            <div className="text-xs text-white">• Full resource partitioning</div>
                            <div className="text-xs text-white">• Custom backup policies</div>
                          </>
                        )}
                        {tier.tier === "High Security Tier" && (
                          <>
                            <div className="text-xs text-white">• Azure GCC region deployment</div>
                            <div className="text-xs text-white">• Complete infrastructure isolation</div>
                            <div className="text-xs text-white">• Air-gapped environment option</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Isolation Architecture Details */}
            <motion.div 
              className="mt-12 bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xl font-bold text-center bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent gradient-text-fix mb-6">
                Data Isolation Architecture
              </h4>
              <div className="grid lg:grid-cols-3 gap-6 text-sm">
                <div>
                  <h5 className="font-semibold text-blue-300 mb-3">Standard Isolation</h5>
                  <ul className="space-y-2 text-white">
                    <li>• Database schemas per tenant</li>
                    <li>• Row-level security policies</li>
                    <li>• Shared compute pool</li>
                    <li>• Platform-managed keys</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-purple-300 mb-3">Enterprise Isolation</h5>
                  <ul className="space-y-2 text-white">
                    <li>• Dedicated databases</li>
                    <li>• Reserved compute instances</li>
                    <li>• Customer-managed keys (CMK)</li>
                    <li>• Custom retention policies</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-indigo-300 mb-3">High Security Isolation</h5>
                  <ul className="space-y-2 text-white">
                    <li>• Isolated infrastructure</li>
                    <li>• Dedicated VNet/Subnet</li>
                    <li>• HSM key management</li>
                    <li>• Compliance certifications</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Cloud-Native Kubernetes Architecture - Clean Style */}
          <motion.div 
            className="mt-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-center text-4xl font-bold mb-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-300 bg-clip-text text-transparent gradient-text-fix">
                Cloud-Native Infrastructure
              </span>
              <br />
              <span className="text-2xl text-white font-normal mt-2 block">
                Enterprise-grade. Infinitely scalable. Actually works.
              </span>
            </motion.h3>

            {/* Main Architecture Overview */}
            <div className="grid lg:grid-cols-2 gap-16 mb-20">
              {/* Infrastructure Stack */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="space-y-8">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-4">Modern Stack That Delivers</h4>
                    <p className="text-lg text-white leading-relaxed">
                      Forget the 47-layer enterprise diagrams. Our infrastructure is clean, 
                      powerful, and actually maintainable. Built on Azure Kubernetes Service 
                      with Cloudflare's edge network protecting everything.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {[
                      {
                        icon: "🌐",
                        title: "Azure API Management",
                        subtitle: "GraphQL & REST at scale",
                        metrics: "< 10ms latency • 99.99% uptime",
                        color: "from-blue-400 to-cyan-300"
                      },
                      {
                        icon: "🔗",
                        title: "Linkerd Service Mesh",
                        subtitle: "Zero-trust networking by default",
                        metrics: "< 1ms overhead • Automatic mTLS",
                        color: "from-purple-400 to-pink-300"
                      },
                      {
                        icon: "📊",
                        title: "OpenTelemetry Stack",
                        subtitle: "See everything, miss nothing",
                        metrics: "10K metrics/sec • 90 day retention",
                        color: "from-emerald-400 to-teal-300"
                      }
                    ].map((item, i) => (
                      <motion.div
                        key={item.title}
                        className="relative group"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="flex gap-4">
                          <div className="text-4xl">{item.icon}</div>
                          <div className="flex-1">
                            <h5 className={`text-xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix mb-1`}>
                              {item.title}
                            </h5>
                            <p className="text-white mb-2">{item.subtitle}</p>
                            <p className="text-sm text-white">{item.metrics}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Security & Compliance */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="space-y-8">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-4">Security Without the Theater</h4>
                    <p className="text-lg text-white leading-relaxed">
                      Real security, not checkbox compliance. Every request validated, 
                      every byte encrypted, every action logged. And yes, we pass all 
                      the audits too.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-8 border border-white/10">
                    <div className="space-y-6">
                      {[
                        {
                          aspect: "Network Defense",
                          value: "Cloudflare WAF",
                          detail: "2M attacks blocked monthly"
                        },
                        {
                          aspect: "Data Protection", 
                          value: "AES-256 + CMK",
                          detail: "Your keys, your control"
                        },
                        {
                          aspect: "Access Control",
                          value: "Zero-Trust RBAC",
                          detail: "Biometric + MFA options"
                        },
                        {
                          aspect: "Compliance",
                          value: "SOC2 Type II",
                          detail: "HIPAA & ISO 27001 ready"
                        }
                      ].map((item, i) => (
                        <motion.div
                          key={item.aspect}
                          className="flex justify-between items-center"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + i * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <div>
                            <div className="text-white font-medium">{item.aspect}</div>
                            <div className="text-sm text-white">{item.detail}</div>
                          </div>
                          <div className="text-right">
                            <div className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent gradient-text-fix font-bold">
                              {item.value}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Three-Layer Architecture */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h4 className="text-2xl font-bold text-center text-white mb-12">
                Three Layers. No BS. Total Control.
              </h4>
              
              <div className="space-y-8">
                {[
                  {
                    layer: "Edge Layer",
                    icon: "🌍",
                    title: "Global Edge Network",
                    description: "Cloudflare's 300+ PoPs handle security, caching, and routing. Your app stays fast everywhere.",
                    features: ["DDoS Protection", "Web Application Firewall", "Smart Routing", "Edge Computing"],
                    color: "from-purple-400 to-indigo-300"
                  },
                  {
                    layer: "Application Layer",
                    icon: "☸️",
                    title: "Kubernetes Orchestration",
                    description: "Azure Kubernetes Service runs your workloads. Auto-scaling, self-healing, zero downtime deployments.",
                    features: ["Container Orchestration", "Service Mesh", "Auto-scaling", "Rolling Updates"],
                    color: "from-blue-400 to-cyan-300"
                  },
                  {
                    layer: "Data Layer",
                    icon: "💾",
                    title: "Intelligent Data Platform",
                    description: "Multi-model databases that scale with your business. Real-time sync, automatic backups, instant recovery.",
                    features: ["Graph + SQL + NoSQL", "Event Sourcing", "Real-time Sync", "Point-in-time Recovery"],
                    color: "from-emerald-400 to-teal-300"
                  }
                ].map((layer, i) => (
                  <motion.div
                    key={layer.layer}
                    className="relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex gap-8 items-start">
                      <motion.div 
                        className="text-5xl"
                        animate={{ 
                          y: [0, -10, 0],
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          delay: i * 0.5
                        }}
                      >
                        {layer.icon}
                      </motion.div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <span className="text-sm text-white">{layer.layer}</span>
                          <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
                        </div>
                        
                        <h5 className={`text-2xl font-bold bg-gradient-to-r ${layer.color} bg-clip-text text-transparent gradient-text-fix mb-3`}>
                          {layer.title}
                        </h5>
                        
                        <p className="text-lg text-white mb-4">
                          {layer.description}
                        </p>
                        
                        <div className="flex flex-wrap gap-3">
                          {layer.features.map((feature) => (
                            <span 
                              key={feature}
                              className="px-3 py-1 text-sm bg-white/5 border border-white/10 rounded-full text-white"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* No-Code Platform Section */}
      <section id="no-code" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              Build Anything Without Code
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-white max-w-5xl mx-auto leading-relaxed mb-6">
              HighPoint's visual development environment puts the power of enterprise software creation 
              directly in your hands. No programming. No IT bottlenecks. No compromise.
            </p>
            <p className="text-base sm:text-lg lg:text-xl text-white max-w-4xl mx-auto">
              From simple workflows to complex business applications, build exactly what your business needs 
              using drag-and-drop simplicity backed by enterprise-grade power.
            </p>
          </motion.div>

          {/* Visual Workflow Builder */}
          <motion.div 
            className="mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="relative">
                  <motion.div
                    className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-r from-purple-500/30 to-indigo-500/30 rounded-full blur-xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  
                  <h3 className="text-4xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent gradient-text-fix">
                    Visual Workflow Designer
                  </h3>
                  
                  <div className="space-y-6">
                    {[
                      {
                        title: "Drag & Drop Logic",
                        description: "Build complex business logic using visual components. Connect triggers, conditions, actions, and approvals with simple drag-and-drop operations.",
                        icon: "🔗"
                      },
                      {
                        title: "Real-Time Testing",
                        description: "Test your workflows instantly with live data. See exactly how your process will behave before deploying to production.",
                        icon: "⚡"
                      },
                      {
                        title: "Multi-System Integration", 
                        description: "Connect any system, any data source, any API. Native connectors for popular tools, plus universal REST/GraphQL integration.",
                        icon: "🌐"
                      },
                      {
                        title: "Advanced Orchestration",
                        description: "Handle complex scenarios: parallel processing, conditional branches, loops, timers, and human approvals in sophisticated workflows.",
                        icon: "🎯"
                      }
                    ].map((feature, i) => (
                      <motion.div
                        key={feature.title}
                        className="flex items-start gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <motion.div 
                          className="text-3xl"
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}
                        >
                          {feature.icon}
                        </motion.div>
                        <div>
                          <h4 className="text-xl font-bold text-white mb-2">{feature.title}</h4>
                          <p className="text-white leading-relaxed">{feature.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 overflow-hidden">
                  <h4 className="text-2xl font-bold text-white mb-6">Sample Workflow: Order Processing</h4>
                  
                  <div className="space-y-4">
                    {[
                      { step: "Order Received", status: "trigger", color: "from-green-500 to-emerald-500" },
                      { step: "Inventory Check", status: "condition", color: "from-blue-500 to-cyan-500" },
                      { step: "Credit Approval", status: "decision", color: "from-yellow-500 to-orange-500" },
                      { step: "Production Schedule", status: "action", color: "from-purple-500 to-indigo-500" },
                      { step: "Ship & Invoice", status: "completion", color: "from-pink-500 to-rose-500" }
                    ].map((step, i) => (
                      <motion.div
                        key={step.step}
                        className="flex items-center gap-4"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <motion.div 
                          className={`w-4 h-4 rounded-full bg-gradient-to-r ${step.color}`}
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                        />
                        <div className="flex-1 bg-black/30 rounded-lg px-4 py-3 border border-white/5">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium">{step.step}</span>
                            <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${step.color} text-black font-bold`}>
                              {step.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        {i < 4 && (
                          <motion.div
                            className="absolute left-6 top-full w-0.5 h-4 bg-white/20"
                            initial={{ height: 0 }}
                            whileInView={{ height: 16 }}
                            transition={{ delay: 0.6 + i * 0.1 }}
                            viewport={{ once: true }}
                          />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Dynamic UI Builder */}
          <motion.div 
            className="mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                className="relative order-2 lg:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
                  <h4 className="text-2xl font-bold text-white mb-6">Dynamic Form Builder</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { type: "Text Input", icon: "📝", color: "from-blue-400 to-cyan-400" },
                      { type: "Dropdown", icon: "📋", color: "from-green-400 to-emerald-400" },
                      { type: "Date Picker", icon: "📅", color: "from-purple-400 to-indigo-400" },
                      { type: "File Upload", icon: "📁", color: "from-orange-400 to-red-400" },
                      { type: "Signature", icon: "✍️", color: "from-pink-400 to-rose-400" },
                      { type: "QR Scanner", icon: "📱", color: "from-teal-400 to-cyan-400" }
                    ].map((component, i) => (
                      <motion.div
                        key={component.type}
                        className={`bg-gradient-to-r ${component.color} p-1 rounded-lg`}
                        whileHover={{ scale: 1.05 }}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="bg-black/80 rounded-md p-3 text-center">
                          <div className="text-2xl mb-1">{component.icon}</div>
                          <div className="text-xs text-white font-medium">{component.type}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-black/30 rounded-lg border border-white/5">
                    <p className="text-white text-sm leading-relaxed">
                      Drag components, set validation rules, configure conditional logic, and deploy instantly. 
                      Forms automatically sync with your workflows and data models.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="order-1 lg:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="relative">
                  <motion.div
                    className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-r from-indigo-500/30 to-blue-500/30 rounded-full blur-xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  />
                  
                  <h3 className="text-4xl font-bold mb-8 bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent gradient-text-fix">
                    Dynamic UI Builder
                  </h3>
                  
                  <div className="space-y-6">
                    {[
                      {
                        title: "Intelligent Page Builder",
                        description: "Create responsive pages, forms, dashboards, and reports with drag-and-drop simplicity. Built-in components for every business need.",
                        icon: "🎨"
                      },
                      {
                        title: "Smart Data Binding", 
                        description: "Connect UI elements to any data source automatically. Real-time updates, validation, and synchronization without code.",
                        icon: "🔄"
                      },
                      {
                        title: "Role-Based Views",
                        description: "Different users see different interfaces based on their role, permissions, and business context. One app, infinite perspectives.",
                        icon: "👥"
                      },
                      {
                        title: "Mobile-First Design",
                        description: "Every page automatically adapts to mobile, tablet, and desktop. Progressive Web App capabilities included.",
                        icon: "📱"
                      }
                    ].map((feature, i) => (
                      <motion.div
                        key={feature.title}
                        className="flex items-start gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <motion.div 
                          className="text-3xl"
                          animate={{ rotate: [0, -10, 10, 0] }}
                          transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}
                        >
                          {feature.icon}
                        </motion.div>
                        <div>
                          <h4 className="text-xl font-bold text-white mb-2">{feature.title}</h4>
                          <p className="text-white leading-relaxed">{feature.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Business Process Examples */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-4xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent gradient-text-fix">
                Real Business Applications
              </h3>
              <p className="text-xl text-white max-w-4xl mx-auto leading-relaxed">
                See how businesses use HighPoint's no-code platform to build mission-critical applications 
                that would typically require months of custom development.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  title: "Quality Management System",
                  description: "Build complete QMS with inspection workflows, non-conformance tracking, CAPA management, and audit trails. FDA 21 CFR Part 11 compliant.",
                  features: ["Document Control", "Change Management", "Audit Workflows", "Supplier Qualification"],
                  icon: "🔍",
                  color: "from-emerald-500 to-teal-500"
                },
                {
                  title: "Field Service Dispatch",
                  description: "Create intelligent dispatch system with route optimization, skill matching, parts management, and mobile technician apps.",
                  features: ["Smart Scheduling", "GPS Tracking", "Inventory Sync", "Customer Portal"],
                  icon: "🚚",
                  color: "from-blue-500 to-cyan-500"
                },
                {
                  title: "Manufacturing Execution",
                  description: "Deploy MES functionality with work order management, machine integration, real-time tracking, and OEE dashboards.",
                  features: ["Work Instructions", "Data Collection", "Genealogy Tracking", "Performance Analytics"],
                  icon: "🏭",
                  color: "from-purple-500 to-indigo-500"
                }
              ].map((example, i) => (
                <motion.div
                  key={example.title}
                  className="relative group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${example.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300 blur-xl`} />
                  
                  <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 h-full flex flex-col">
                    <motion.div 
                      className="text-5xl mb-6 flex-shrink-0"
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                    >
                      {example.icon}
                    </motion.div>
                    
                    <h4 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${example.color} bg-clip-text text-transparent gradient-text-fix`}>
                      {example.title}
                    </h4>
                    
                    <div className="flex-grow">
                      <p className="text-white mb-6 leading-relaxed">
                        {example.description}
                      </p>
                      
                      <div className="space-y-3">
                        {example.features.map((feature, j) => (
                          <motion.div
                            key={feature}
                            className="flex items-center gap-3"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 + i * 0.2 + j * 0.1 }}
                            viewport={{ once: true }}
                          >
                            <motion.div 
                              className={`w-2 h-2 rounded-full bg-gradient-to-r ${example.color} flex-shrink-0`}
                              animate={{ scale: [1, 1.5, 1] }}
                              transition={{ duration: 2, repeat: Infinity, delay: j * 0.3 }}
                            />
                            <span className="text-white">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-purple-900/50 via-indigo-900/50 to-blue-900/50 backdrop-blur-xl border border-white/10 rounded-3xl p-12">
              <motion.h3 
                className="text-4xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 bg-clip-text text-transparent gradient-text-fix"
                animate={{ backgroundPosition: ["0%", "100%", "0%"] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                Start Building Today
              </motion.h3>
              
              <p className="text-2xl text-white mb-8 max-w-4xl mx-auto leading-relaxed">
                Stop waiting for IT. Stop compromising on functionality. Stop accepting "that's just how enterprise software works."
              </p>
              
              <p className="text-xl text-white mb-10 max-w-3xl mx-auto">
                With HighPoint's no-code platform, you can build the exact solution your business needs, 
                deploy it in days instead of months, and evolve it as your business grows.
              </p>
              
              <motion.button
                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-12 py-4 rounded-xl font-bold text-lg shadow-lg shadow-purple-500/25 transition-all duration-300"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(147, 51, 234, 0.4)" }}
                whileTap={{ scale: 0.95 }}
              >
                Experience No-Code Power
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* ERP/MES Comparison Section */}
      <section id="comparison" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-orange-400 to-red-300 bg-clip-text text-transparent gradient-text-fix">
              Why HighPoint Beats Traditional ERP/MES
            </h2>
            <p className="text-xl text-text-secondary max-w-4xl mx-auto text-adaptive mb-4">
              Stop paying millions for software that fights you every step of the way. 
              HighPoint delivers what legacy systems promised but never delivered.
            </p>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto text-adaptive opacity-90">
              (Spoiler: It's because we actually talked to the people who use this stuff.)
            </p>
          </motion.div>

          {/* The Reality Check */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* HighPoint First */}
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-300 via-cyan-200 to-emerald-200 bg-clip-text text-transparent gradient-text-fix">
                HighPoint
              </h3>
              <p className="text-lg sm:text-xl lg:text-2xl text-cyan-300 font-semibold mb-8">Modern Business Process Orchestration</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto">
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix">8-12</div>
                  <div className="text-white font-semibold text-sm sm:text-base">weeks to deploy</div>
                </motion.div>
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent gradient-text-fix">75%</div>
                  <div className="text-white font-semibold text-sm sm:text-base">cost reduction</div>
                </motion.div>
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix">2-3</div>
                  <div className="text-white font-semibold text-sm sm:text-base">days training</div>
                </motion.div>
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-orange-400 to-red-300 bg-clip-text text-transparent gradient-text-fix">Zero</div>
                  <div className="text-white font-semibold text-sm sm:text-base">vendor lock-in</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Comparison Table */}
            <div className="space-y-1">
              {/* Header Row */}
              <motion.div 
                className="hidden lg:grid grid-cols-4 gap-8 mb-8"
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div></div> {/* Empty corner cell */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-green-400 text-xl lg:text-2xl font-bold">✓</span>
                    <h4 className="text-lg lg:text-2xl font-bold text-white">HighPoint</h4>
                  </div>
                  <p className="text-green-300 font-semibold text-sm lg:text-base">What Actually Works</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-red-500 text-xl lg:text-2xl font-bold">✗</span>
                    <h4 className="text-lg lg:text-2xl font-bold text-white">Traditional ERP</h4>
                  </div>
                  <p className="text-red-300 font-semibold text-sm lg:text-base">The Legacy Nightmare</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-red-500 text-xl lg:text-2xl font-bold">✗</span>
                    <h4 className="text-lg lg:text-2xl font-bold text-white">MES Systems</h4>
                  </div>
                  <p className="text-orange-300 font-semibold text-sm lg:text-base">Shop Floor Islands</p>
                </div>
              </motion.div>

              {/* Mobile Header */}
              <motion.div 
                className="lg:hidden text-center mb-8"
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h4 className="text-2xl font-bold text-white mb-4">Platform Comparison</h4>
                <p className="text-white text-sm opacity-80">Tap each category to see the comparison</p>
              </motion.div>

              {/* Table Rows */}
              {[
                {
                  category: "Implementation Speed",
                  icon: "⚡",
                  color: "from-blue-400 to-cyan-300",
                  highpoint: { feature: "8-12 weeks deployment", benefit: "You'll start seeing ROI in months, not years" },
                  erp: { issue: "18-36 months implementation", pain: "Years of business disruption" },
                  mes: { issue: "6-12 months setup time", pain: "Extended deployment pain" }
                },
                {
                  category: "Total Investment",
                  icon: "💰",
                  color: "from-green-400 to-emerald-300",
                  highpoint: { feature: "75% cost reduction", benefit: "You'll invest savings in growth, not software" },
                  erp: { issue: "$2-10M+ total cost", pain: "Budget obliteration" },
                  mes: { issue: "$500K-2M investment", pain: "Major capital expense" }
                },
                {
                  category: "Support & Dependency",
                  icon: "🤝",
                  color: "from-purple-400 to-pink-300",
                  highpoint: { feature: "Zero consultant dependency", benefit: "Your team owns the solution" },
                  erp: { issue: "Army of consultants required", pain: "Permanent vendor dependency" },
                  mes: { issue: "Complete vendor lock-in", pain: "Proprietary prison forever" }
                },
                {
                  category: "User Training",
                  icon: "🎓",
                  color: "from-orange-400 to-red-300",
                  highpoint: { feature: "2-3 days training", benefit: "Productive immediately, not eventually" },
                  erp: { issue: "2-6 weeks per user training", pain: "Productivity destruction" },
                  mes: { issue: "Complex system learning", pain: "Steep learning curves" }
                },
                {
                  category: "Customization",
                  icon: "🎨",
                  color: "from-teal-400 to-cyan-300",
                  highpoint: { feature: "Unlimited customization", benefit: "Adapts to your business, not vice versa" },
                  erp: { issue: "Rigid, limited customization", pain: "Business process jail" },
                  mes: { issue: "Shop floor only coverage", pain: "Siloed business operations" }
                },
                {
                  category: "Mobile Experience",
                  icon: "📱",
                  color: "from-indigo-400 to-purple-300",
                  highpoint: { feature: "Native mobile apps", benefit: "Work from anywhere, anytime" },
                  erp: { issue: "Terrible mobile experience", pain: "Desktop prisoners" },
                  mes: { issue: "No real mobile support", pain: "Chained to workstations" }
                }
              ].slice(0, showSimplifiedContent ? 4 : undefined).map((comparison, i) => (
                <motion.div
                  key={comparison.category}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  {/* Desktop Table View */}
                  <div className="hidden lg:grid grid-cols-4 gap-8 py-6">
                    {/* Category Column */}
                    <div className="flex items-center gap-4">
                      <span className="text-2xl lg:text-3xl">{comparison.icon}</span>
                      <h5 className={`text-base lg:text-lg font-bold bg-gradient-to-r ${comparison.color} bg-clip-text text-transparent gradient-text-fix`}>
                        {comparison.category}
                      </h5>
                    </div>

                    {/* HighPoint Column */}
                    <div className="text-center">
                      <div className="text-sm lg:text-base font-semibold mb-1 bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.highpoint.feature}
                      </div>
                      <div className="text-white text-xs lg:text-sm">
                        {comparison.highpoint.benefit}
                      </div>
                    </div>

                    {/* Traditional ERP Column */}
                    <div className="text-center">
                      <div className="text-sm lg:text-base font-semibold mb-1 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.erp.issue}
                      </div>
                      <div className="text-white text-xs lg:text-sm">
                        {comparison.erp.pain}
                      </div>
                    </div>

                    {/* MES Systems Column */}
                    <div className="text-center">
                      <div className="text-sm lg:text-base font-semibold mb-1 bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.mes.issue}
                      </div>
                      <div className="text-white text-xs lg:text-sm">
                        {comparison.mes.pain}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-6 py-6">
                    {/* Category Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-3xl">{comparison.icon}</span>
                      <h5 className={`text-xl font-bold bg-gradient-to-r ${comparison.color} bg-clip-text text-transparent gradient-text-fix`}>
                        {comparison.category}
                      </h5>
                    </div>

                    {/* HighPoint Card */}
                    <div className="bg-black/30 border border-green-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-green-400 text-xl font-bold">✓</span>
                        <h6 className="text-lg font-bold text-white">HighPoint</h6>
                      </div>
                      <div className="text-base font-semibold mb-2 bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.highpoint.feature}
                      </div>
                      <div className="text-white text-sm">
                        {comparison.highpoint.benefit}
                      </div>
                    </div>

                    {/* Traditional ERP Card */}
                    <div className="bg-black/30 border border-red-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-red-500 text-xl font-bold">✗</span>
                        <h6 className="text-lg font-bold text-white">Traditional ERP</h6>
                      </div>
                      <div className="text-base font-semibold mb-2 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.erp.issue}
                      </div>
                      <div className="text-white text-sm">
                        {comparison.erp.pain}
                      </div>
                    </div>

                    {/* MES Systems Card */}
                    <div className="bg-black/30 border border-orange-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-red-500 text-xl font-bold">✗</span>
                        <h6 className="text-lg font-bold text-white">MES Systems</h6>
                      </div>
                      <div className="text-base font-semibold mb-2 bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent gradient-text-fix">
                        {comparison.mes.issue}
                      </div>
                      <div className="text-white text-sm">
                        {comparison.mes.pain}
                      </div>
                    </div>
                  </div>

                  {/* Separation Line */}
                  {i < 5 && (
                    <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  )}
                </motion.div>
              ))}
            </div>
            
            {/* Show more on mobile */}
            {showSimplifiedContent && (
              <div className="text-center mb-12">
                <p className="text-white/60 text-sm">
                  + 8 more detailed comparisons available on desktop view
                </p>
              </div>
            )}

            {/* The Bottom Line */}
            <motion.div 
              className="text-center mt-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h4 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8">The Bottom Line</h4>
              <p className="text-lg sm:text-xl lg:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
                Stop paying millions for software that fights you. Stop waiting years for implementations that fail. 
                Stop accepting "that's just how enterprise software works."
              </p>
              <p className="text-base sm:text-lg lg:text-xl text-cyan-300 mt-6 font-semibold">
                HighPoint will deliver what they promised but never could.
              </p>
            </motion.div>
          </motion.div>

          {/* The HAIDEN Difference - Clean Style */}
          <motion.div 
            className="mt-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-center text-4xl font-bold mb-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-300 bg-clip-text text-transparent gradient-text-fix">
                The HAIDEN Difference
              </span>
              <br />
              <span className="text-2xl text-white font-normal mt-2 block">
                Being built by people who've actually done this before
              </span>
            </motion.h3>

            <div className="grid lg:grid-cols-2 gap-16">
              {/* Why We're Different */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="space-y-8">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-4">
                      We've Lived Your Pain
                    </h4>
                    <p className="text-lg text-white leading-relaxed">
                      Our architect designed industry-leading manufacturing platforms used globally. 
                      We know exactly where traditional systems fail because we've been there, 
                      fighting the same battles you're facing now.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {[
                      {
                        icon: "🎯",
                        title: "Your Workflows, Not Ours",
                        description: "ERP vendors force you into their box. We'll start with how you actually work and build around that.",
                        color: "from-orange-400 to-amber-300"
                      },
                      {
                        icon: "💡",
                        title: "Glass Box, Not Black Box",
                        description: "You'll see exactly how your data flows, what automations do, and why decisions are made. Complete transparency.",
                        color: "from-amber-400 to-yellow-300"
                      },
                      {
                        icon: "🚀",
                        title: "Speed Without Sacrifice",
                        description: "You'll go live in weeks with core functionality. Add features as you need them. No 18-month implementations.",
                        color: "from-yellow-400 to-lime-300"
                      }
                    ].map((item, i) => (
                      <motion.div
                        key={item.title}
                        className="relative"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="flex gap-4">
                          <div className="text-4xl">{item.icon}</div>
                          <div className="flex-1">
                            <h5 className={`text-xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix mb-2`}>
                              {item.title}
                            </h5>
                            <p className="text-white">{item.description}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Technical Excellence */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="space-y-8">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-4">
                      Architecture That Makes Sense
                    </h4>
                    <p className="text-lg text-white leading-relaxed">
                      No over-engineered complexity. No vendor lock-in. Just clean, 
                      powerful architecture that your team can actually understand and maintain.
                    </p>
                  </div>

                  {/* Core Architecture */}
                  <div className="space-y-4">
                    <h5 className="text-lg font-semibold text-white">Built Right From Day One</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { label: "Multi-Tenant", value: "Isolated by Design" },
                        { label: "Event-Driven", value: "CQRS + Sourcing" },
                        { label: "API-First", value: "GraphQL Federation" },
                        { label: "Cloud Native", value: "Kubernetes on AKS" }
                      ].map((item) => (
                        <div key={item.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                          <div className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent gradient-text-fix font-bold">
                            {item.label}
                          </div>
                          <div className="text-sm text-white">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Integrations */}
                  <div className="space-y-4">
                    <h5 className="text-lg font-semibold text-white">Connects to Everything</h5>
                    <div className="space-y-3">
                      {[
                        "PSA Systems - Full bi-directional sync",
                        "CRM Platforms - Real-time data flow",
                        "Office Suites - Seamless collaboration",
                        "Custom APIs - Any system, any format"
                      ].map((integration, i) => (
                        <motion.div
                          key={integration}
                          className="flex items-center gap-3 text-white"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + i * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-400 to-amber-400" />
                          <span>{integration}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Line */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="inline-block bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl p-8 border border-white/10">
                <p className="text-xl text-white font-medium mb-2">
                  The Bottom Line:
                </p>
                <p className="text-2xl bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent gradient-text-fix font-bold">
                  We're building software the way we wish someone had built it for us.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Integration Ecosystem Section */}
      <section className="py-32 relative bg-gradient-to-b from-transparent via-green-950/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent gradient-text-fix">
              Connect Everything, Control Everything
            </h2>
            <p className="text-xl text-white max-w-4xl mx-auto">
              Pre-built connectors for the tools you already use. No more data silos, 
              no more manual exports, no more "integration consultants."
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Native Connectors */}
            <motion.div 
              className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
                <span className="text-2xl">🔌</span>
                Native Connectors
              </h3>
              <div className="space-y-4">
                {[
                  { 
                    name: "HaloPSA", 
                    features: ["Ticket Sync", "Asset Management", "Project Tracking", "Billing Integration"],
                    icon: "🎫"
                  },
                  { 
                    name: "HubSpot", 
                    features: ["Contact Sync", "Deal Pipeline", "Activity Tracking", "Marketing Data"],
                    icon: "🎯"
                  },
                  { 
                    name: "Microsoft 365", 
                    features: ["Document Integration", "Teams Collaboration", "SharePoint Access", "Calendar Sync"],
                    icon: "📊"
                  },
                  { 
                    name: "Google Workspace", 
                    features: ["Drive Integration", "Calendar Sync", "Gmail Integration", "Contacts"],
                    icon: "📧"
                  }
                ].map((connector, i) => (
                  <motion.div 
                    key={connector.name}
                    className="border-l-2 border-green-400/50 pl-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + i * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-2 font-medium text-white mb-1">
                      <span>{connector.icon}</span>
                      <span>{connector.name}</span>
                    </div>
                    <div className="text-xs text-white">{connector.features.join(" • ")}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* IT Tool Connectors */}
            <motion.div 
              className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
                <span className="text-2xl">🛠️</span>
                IT Management Tools
              </h3>
              <div className="space-y-4">
                {[
                  { 
                    name: "NinjaRMM", 
                    desc: "Remote monitoring & management",
                    features: ["Device monitoring", "Patch management", "Remote access"],
                    icon: "🥷"
                  },
                  { 
                    name: "SentinelOne", 
                    desc: "Endpoint security & EDR",
                    features: ["Threat detection", "Automated response", "Forensics"],
                    icon: "🛡️"
                  },
                  { 
                    name: "Intune", 
                    desc: "Device management & compliance",
                    features: ["Policy enforcement", "App deployment", "Compliance reporting"],
                    icon: "📱"
                  }
                ].map((tool, i) => (
                  <motion.div 
                    key={tool.name}
                    className="border-l-2 border-blue-400/50 pl-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-2 font-medium text-white mb-1">
                      <span>{tool.icon}</span>
                      <span>{tool.name}</span>
                    </div>
                    <div className="text-sm text-white mb-1">{tool.desc}</div>
                    <div className="text-xs text-white">{tool.features.join(" • ")}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Integration Framework */}
            <motion.div 
              className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
                <span className="text-2xl">⚙️</span>
                Integration Engine
              </h3>
              <div className="space-y-3">
                {[
                  { feature: "Real-time data synchronization", desc: "Instant updates across all systems" },
                  { feature: "Intelligent data mapping", desc: "Auto-map fields between systems" },
                  { feature: "Error handling & recovery", desc: "Automatic retry with backoff" },
                  { feature: "Sync status monitoring", desc: "Full visibility into data flow" },
                  { feature: "Custom connector SDK", desc: "Build your own integrations" },
                  { feature: "Webhook support", desc: "Event-driven architecture" }
                ].map((item, i) => (
                  <motion.div 
                    key={item.feature}
                    className="flex items-start gap-2"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5" />
                    <div>
                      <div className="text-sm text-white">{item.feature}</div>
                      <div className="text-xs text-white">{item.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          
          {/* Integration Benefits */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl p-8 border border-white/10">
              <p className="text-lg text-white mb-2">
                All integrations support bi-directional sync with
              </p>
              <p className="text-2xl bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent gradient-text-fix font-bold">
                Field mapping • Conflict resolution • Real-time updates • Full audit trail
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Developer Section */}
      <section id="creator" className="py-32 relative bg-gradient-to-b from-transparent via-purple-950/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="inline-block px-6 py-2 mb-6 rounded-full border border-purple-500/30 bg-gradient-to-r from-purple-950/40 to-pink-950/40 backdrop-blur-xl"
              whileHover={{ scale: 1.05, borderColor: "rgba(168, 85, 247, 0.5)" }}
            >
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix uppercase tracking-wider">
                SOLO DEVELOPER + AI TEAM
              </span>
            </motion.div>
            <h2 className="font-display text-title font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent gradient-text-fix">
              One Developer. Multiple AIs. Zero BS.
            </h2>
            <p className="text-xl text-white max-w-4xl mx-auto mb-4">
              HighPoint isn't built by a massive team burning through VC money. 
              It's one motivated developer leveraging AI to build what enterprises actually need.
            </p>
            <p className="text-lg text-white max-w-3xl mx-auto">
              No committees. No bureaucracy. Just solutions that work.
            </p>
          </motion.div>

          {/* The Solo + AI Revolution */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <motion.h3 
                  className="text-5xl font-bold mb-8 leading-tight"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 bg-clip-text text-transparent gradient-text-fix">
                    The Future of
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text text-transparent gradient-text-fix">
                    Enterprise Development
                  </span>
                </motion.h3>
                
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <p className="text-xl text-white leading-relaxed">
                    Traditional enterprise software development is broken. Teams of hundreds burning 
                    millions to deliver systems that users hate. We're proving there's a better way.
                  </p>
                  <p className="text-lg text-white leading-relaxed">
                    One experienced developer paired with specialized AI assistants can deliver what 
                    used to take entire departments. Not through shortcuts or compromise, but through 
                    intelligent automation and focused execution.
                  </p>
                  <p className="text-lg text-white leading-relaxed">
                    This isn't about replacing developers. It's about amplifying human expertise with 
                    AI capabilities. While traditional teams debate in meetings, we're shipping features 
                    that actually work.
                  </p>
                </motion.div>
              </div>
              
              <motion.div
                className="relative"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="absolute -inset-20 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-cyan-500/20 rounded-full blur-3xl"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                <div className="relative space-y-8">
                  {[
                    {
                      icon: "🧠",
                      title: "AI Architecture",
                      subtitle: "System design that scales",
                      color: "from-cyan-400 to-blue-300",
                      delay: 0
                    },
                    {
                      icon: "⚡",
                      title: "Instant Iteration",
                      subtitle: "Ideas to implementation in hours",
                      color: "from-purple-400 to-pink-300",
                      delay: 0.5
                    },
                    {
                      icon: "🎯",
                      title: "Zero Waste",
                      subtitle: "Every line of code has purpose",
                      color: "from-pink-400 to-orange-300",
                      delay: 1
                    }
                  ].map((item, i) => (
                    <motion.div
                      key={item.title}
                      className="flex items-center gap-6"
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <motion.div
                        className="text-6xl"
                        animate={{ 
                          rotate: [0, 10, -10, 0],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{ 
                          duration: 4,
                          repeat: Infinity,
                          delay: item.delay
                        }}
                      >
                        {item.icon}
                      </motion.div>
                      <div className="text-left">
                        <h4 className={`text-2xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix`}>
                          {item.title}
                        </h4>
                        <p className="text-white">{item.subtitle}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>


          {/* Transition to Bio */}
          <motion.div 
            className="mb-20 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="inline-block px-6 py-2 mb-6 rounded-full border border-blue-500/30 bg-gradient-to-r from-blue-950/40 to-cyan-950/40 backdrop-blur-xl"
              whileHover={{ scale: 1.05, borderColor: "rgba(59, 130, 246, 0.5)" }}
            >
              <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix uppercase tracking-wider">
                EXECUTIVE PROFILE
              </span>
            </motion.div>
            <motion.h3 
              className="text-4xl font-bold mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="bg-gradient-to-r from-purple-400 via-blue-300 to-cyan-300 bg-clip-text text-transparent gradient-text-fix">
                Meet the Human Behind the Vision
              </span>
            </motion.h3>
            <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
              Behind every great platform is a leader who understands both technology and people. 
              Meet the cybersecurity expert and systems architect driving this AI-powered revolution.
            </p>
          </motion.div>

          {/* Mike Stafford Bio */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-16">
              <h3 className="font-display text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
                Mike Stafford
              </h3>
              <p className="text-2xl text-white max-w-4xl mx-auto mb-4 font-medium">
                Strategic technology & cyber leader who converts chaotic, high-risk operations into secure, automated, people-centric ecosystems—while preserving both culture and ROI.
              </p>
              <p className="text-lg text-white max-w-3xl mx-auto">
                15+ years transforming organizations through cybersecurity excellence, cloud architecture, and values-driven leadership.
              </p>
            </div>

          {/* Professional Overview */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <motion.h3 
                  className="text-4xl font-bold mb-8 leading-tight"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <span className="bg-gradient-to-r from-blue-300 via-cyan-300 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
                    Cybersecurity Authority &
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-teal-300 via-emerald-300 to-blue-300 bg-clip-text text-transparent gradient-text-fix">
                    Systems Architect
                  </span>
                </motion.h3>
                
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-cyan-400 mb-2">15+</div>
                      <div className="text-sm text-white uppercase tracking-wider">Years Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-400 mb-2">Zero</div>
                      <div className="text-sm text-white uppercase tracking-wider">Unplanned Downtime</div>
                    </div>
                  </div>
                  
                  <p className="text-lg text-white leading-relaxed">
                    CISSP • CISM • CMMC CCP certified professional who orchestrates NIST 800-171 
                    frameworks and zero-trust architectures across multi-entity portfolios.
                  </p>
                  <p className="text-lg text-white leading-relaxed">
                    Led complete Azure cloud transformations with minimal business impact, 
                    while reducing device onboarding from 8 hours to 15 minutes 
                    through intelligent automation.
                  </p>
                </motion.div>
              </div>
              
              <motion.div
                className="relative"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="absolute -inset-20 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-teal-500/20 rounded-full blur-3xl"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                <div className="relative space-y-8">
                  {[
                    {
                      icon: "🛡️",
                      title: "Security Posture",
                      subtitle: "Halved incident recurrence via SIEM & EDR",
                      color: "from-blue-400 to-cyan-300",
                    },
                    {
                      icon: "☁️",
                      title: "Cloud Excellence",
                      subtitle: "Enterprise cloud transformations delivered",
                      color: "from-cyan-400 to-teal-300",
                    },
                    {
                      icon: "⚡",
                      title: "Automation",
                      subtitle: "Hundreds of technician hours saved annually",
                      color: "from-teal-400 to-emerald-300",
                    }
                  ].map((item, i) => (
                    <motion.div
                      key={item.title}
                      className="flex items-center gap-6"
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <motion.div
                        className="text-5xl"
                        animate={{ 
                          rotate: [0, 10, -10, 0],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{ 
                          duration: 4,
                          repeat: Infinity,
                          delay: i * 0.5
                        }}
                      >
                        {item.icon}
                      </motion.div>
                      <div className="text-left">
                        <h4 className={`text-xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent gradient-text-fix`}>
                          {item.title}
                        </h4>
                        <p className="text-white text-sm">{item.subtitle}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Leadership Philosophy */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-center text-4xl font-bold mb-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent gradient-text-fix">
                Leadership Philosophy
              </span>
            </motion.h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  title: "People-First Security",
                  description: "Technology succeeds only when humans feel informed and respected.",
                  icon: "👥",
                  color: "from-blue-400 to-cyan-300"
                },
                {
                  title: "Transparency Over Silence", 
                  description: "Clear escalation paths beat quiet compliance every time.",
                  icon: "🔍",
                  color: "from-cyan-400 to-teal-300"
                },
                {
                  title: "Automate Relentlessly",
                  description: "Human creativity should never be spent on repetitive tasks.",
                  icon: "⚙️",
                  color: "from-teal-400 to-emerald-300"
                },
                {
                  title: "Integrity Is Non-Negotiable",
                  description: "Ethical shortcuts erode value faster than any hacker.",
                  icon: "🛡️",
                  color: "from-emerald-400 to-green-300"
                },
                {
                  title: "Data-Driven Decisions",
                  description: "Evidence eclipses opinion; metrics guide every initiative.",
                  icon: "📊",
                  color: "from-green-400 to-blue-300"
                },
                {
                  title: "Values-First Negotiation",
                  description: "Ethics yield ROI—converting contentious exits into consulting retainers.",
                  icon: "🤝",
                  color: "from-blue-400 to-purple-300"
                }
              ].map((principle, i) => (
                <motion.div
                  key={principle.title}
                  className="relative p-8 text-center h-full flex flex-col border-l-2 border-white/20 hover:border-white/40 transition-colors duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ x: 8 }}
                >
                  <motion.div
                    className="text-5xl mb-6"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {principle.icon}
                  </motion.div>
                  <h4 className={`text-xl font-bold mb-4 bg-gradient-to-r ${principle.color} bg-clip-text text-transparent gradient-text-fix`}>
                    {principle.title}
                  </h4>
                  <p className="text-white text-base leading-relaxed flex-grow">
                    {principle.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Professional Credentials */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-3xl font-bold mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <span className="bg-gradient-to-r from-amber-400 via-orange-300 to-red-300 bg-clip-text text-transparent gradient-text-fix">
                Certifications & Expertise
              </span>
            </motion.h3>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                "CISSP", "CISM", "CMMC CCP", 
                "Microsoft 365 Enterprise Admin Expert", 
                "Microsoft Cybersecurity Architect Expert",
                "SANS GCFA (exam 2025)"
              ].map((cert, i) => (
                <motion.span
                  key={cert}
                  className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 text-white text-sm font-medium backdrop-blur-xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  {cert}
                </motion.span>
              ))}
            </div>
            
            <motion.p 
              className="text-lg text-white max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              <span className="font-semibold text-cyan-400">Founder, HAIDEN Technologies</span> — 
              Building transparent AI & cyber solutions that empower rather than restrict. 
              The rare fusion of cyber guardian, systems architect, and behavioral-intelligence coach.
            </motion.p>
          </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - Enhanced */}
      <section id="faq" className="py-32 relative bg-gradient-to-b from-transparent via-indigo-950/5 to-transparent">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-20">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-5xl font-bold mb-6 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent gradient-text-fix gradient-text-fix">
              Everything You Want to Know
            </h2>
            <p className="text-xl text-white max-w-4xl mx-auto leading-relaxed mb-4">
              No marketing fluff. No evasive answers. Just the honest truth about HighPoint, 
              enterprise software, and why most platforms suck.
            </p>
            <p className="text-lg text-white max-w-3xl mx-auto">
              These are the actual questions we get asked. Here are our unfiltered answers.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                category: "Platform & Technology",
                icon: "⚙️",
                color: "from-blue-400 to-cyan-400",
                questions: [
                  {
                    q: "How is HighPoint different from Salesforce, SAP, or Oracle?",
                    a: "Those platforms try to be everything to everyone, resulting in bloated, expensive systems that require armies of consultants. HighPoint focuses on one thing: orchestrating your business processes intelligently. We connect to those systems if you have them, but we don't try to replace your entire tech stack. Think of us as the conductor, not the entire orchestra.",
                    details: "Salesforce charges $300/user/month for enterprise features. SAP implementations take 18+ months and cost millions. We're 75% cheaper and deploy in 8-12 weeks."
                  },
                  {
                    q: "What if we already have an ERP system?",
                    a: "HighPoint typically replaces traditional ERP systems entirely – that's usually the best approach. Legacy ERPs like SAP, Oracle, and NetSuite are expensive, inflexible, and built for 1990s business models. HighPoint provides all core ERP functions (financials, procurement, inventory, order management) plus modern workflow orchestration and real-time intelligence. If you must keep your existing ERP, we can integrate with it, but most customers find replacing it delivers better ROI.",
                    details: "HighPoint includes native ERP modules: General Ledger, AP/AR, Purchase Orders, Inventory Management, Order Management, and Financial Reporting. Plus MES, QMS, CRM, FSM, CMSS, and PSA functions integrated seamlessly."
                  },
                  {
                    q: "Is this another 'AI-powered' marketing gimmick?",
                    a: "We use AI where it actually helps: predicting bottlenecks, optimizing workflows, and catching anomalies. But it's not magic fairy dust we sprinkle on everything. Every AI decision is explainable, overrideable, and transparent. No black boxes making mysterious decisions about your business.",
                    details: "Our AI models are trained on manufacturing and business process data, not generic internet content. You can see why each recommendation was made and override any decision."
                  },
                  {
                    q: "What about our custom processes?",
                    a: "That's literally why we built HighPoint. Every business has unique workflows – if they didn't, you'd have no competitive advantage. Our no-code workflow builder lets you model any process, no matter how specific to your business. And when you need to change it? Takes minutes, not months.",
                    details: "Built on Temporal workflow engine with visual designer. Handle complex approvals, multi-system integrations, and conditional logic without writing code."
                  },
                  {
                    q: "How secure is HighPoint really?",
                    a: "Industry-standard encryption, aligning with NIST 800-171 standards, regular penetration testing, and zero-trust architecture. Your data is encrypted at rest and in transit, with customer-managed keys available. We follow NIST cybersecurity frameworks and undergo quarterly security audits.",
                    details: "Three isolation tiers: Standard (logical), Enterprise (dedicated resources), High Security (air-gapped). Choose your level based on compliance needs."
                  }
                ]
              },
              {
                category: "Implementation & Business",
                icon: "🚀",
                color: "from-emerald-400 to-teal-400", 
                questions: [
                  {
                    q: "How long does implementation really take?",
                    a: "8-12 weeks for a typical deployment. Yes, really. We start with your most painful workflow, get it running in 2-3 weeks, then build from there. No 'big bang' launches, no 18-month projects that never finish. If a vendor tells you it'll take a year, they're planning to milk you for consulting fees.",
                    details: "Week 1-2: Discovery and setup. Week 3-4: First workflow live. Week 5-12: Additional workflows and integrations. You see value immediately, not after a year."
                  },
                  {
                    q: "How much does HighPoint actually cost?",
                    a: "About 75% less than traditional enterprise software over 5 years. We charge a transparent monthly fee based on your usage – no hidden modules, no surprise fees, no 'contact sales for pricing' BS. Most customers see positive ROI within 6 months just from efficiency gains.",
                    details: "Standard tier: $100/user/month. Enterprise: Contact sales. No setup fees, no consulting requirements, no vendor lock-in. Export your data anytime."
                  },
                  {
                    q: "What happens to our data if we leave?",
                    a: "It stays yours. Period. Export it anytime in standard formats (JSON, CSV, XML). We use open standards specifically so you're never trapped. And if you decide to leave? Here's your data, thanks for the memories, no hard feelings. (Though our 98% retention rate suggests you won't want to.)",
                    details: "Complete data export includes all historical events, relationships, and configurations. No proprietary formats, no ransom fees, no 'migration consultants' required."
                  },
                  {
                    q: "Who's actually behind HighPoint?",
                    a: "The same architect who designed PSI's manufacturing platform that runs in thousands of factories worldwide. We're not MBA consultants theorizing about business – we've been in the trenches, felt the pain, and built something better. HAIDEN Technologies is our way of bringing enterprise-grade capability to everyone.",
                    details: "30+ years of enterprise software experience. Former PSI architect, Microsoft veteran, built systems for Fortune 500 manufacturers. Solo developer + AI team approach."
                  },
                  {
                    q: "What if we need help or training?",
                    a: "The system handles all T1 and T2 level support requests automatically through self-healing and development capabilities. Human engineers only get involved when the system can't self-heal, develop itself, or needs further instruction. For training, our AI-powered guidance system walks your team through each process, adapting to their learning pace. Most users are productive within hours, not days.",
                    details: "Automated issue resolution, self-updating documentation, contextual help, and adaptive training paths. When human support is needed, you get direct access to the engineers who built the system."
                  }
                ]
              }
            ].slice(0, showSimplifiedContent ? 1 : undefined).map((section, sectionIndex) => (
              <motion.div
                key={section.category}
                className="space-y-6"
                initial={{ opacity: 0, x: sectionIndex === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="text-center mb-8">
                  <motion.div 
                    className="text-4xl mb-3"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: sectionIndex * 0.5 }}
                  >
                    {section.icon}
                  </motion.div>
                  <h3 className={`text-2xl font-bold bg-gradient-to-r ${section.color} bg-clip-text text-transparent gradient-text-fix`}>
                    {section.category}
                  </h3>
                </div>
                
                {section.questions.map((faq, i) => (
                  <motion.div
                    key={i}
                    className="relative group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${section.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
                    <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full">
                      <div className="flex items-start gap-3 mb-4">
                        <motion.div 
                          className={`w-6 h-6 rounded-full bg-gradient-to-r ${section.color} flex items-center justify-center text-xs font-bold text-black mt-1`}
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.2 }}
                        >
                          Q
                        </motion.div>
                        <h4 className="text-lg font-bold text-white leading-tight">
                          {faq.q}
                        </h4>
                      </div>
                      
                      <div className="ml-9 space-y-4">
                        <p className="text-white leading-relaxed">
                          {faq.a}
                        </p>
                        
                        <div className="bg-black/30 rounded-lg p-4 border-l-2 border-l-gradient-to-b border-opacity-50" style={{borderLeftColor: section.color.includes('blue') ? '#60a5fa' : section.color.includes('emerald') ? '#34d399' : '#a855f7'}}>
                          <p className="text-sm text-white leading-relaxed">
                            <span className="font-medium text-white">Details:</span> {faq.details}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ))}
          </div>
          
          {/* Call-to-Action for FAQ */}
          <motion.div 
            className="mt-20 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-8 border border-white/10">
              <p className="text-lg text-white mb-3">
                Still have questions?
              </p>
              <p className="text-2xl bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent gradient-text-fix font-bold mb-4">
                Let's have an honest conversation about your business processes.
              </p>
              <p className="text-white">
                No sales pitch. No canned demo. Just a real discussion about what you're trying to solve.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Call to Action Section */}
      <section id="contact" className="py-32 relative">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center relative z-20">
          <motion.div 
            className="bg-gradient-to-br from-indigo-950/30 to-purple-950/30 backdrop-blur-xl border border-indigo-500/20 rounded-3xl p-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-400 to-purple-300 bg-clip-text text-transparent gradient-text-fix">
              Ready to See the Future?
            </h2>
            <p className="text-lg text-text-secondary mb-8 text-adaptive">
              Get early access to HighPoint and see what enterprise software should have been all along.
            </p>
            
            <form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="your@email.com" 
                  className="flex-1 px-6 py-4 bg-black/50 border border-white/10 rounded-xl text-adaptive placeholder:text-gray-500 focus:outline-none focus:border-indigo-500/50 transition-colors"
                  required
                />
                <motion.button 
                  type="submit"
                  className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 shadow-lg shadow-indigo-500/25"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Access
                </motion.button>
              </div>
              <p className="text-sm text-text-secondary mt-4 text-adaptive opacity-70">
                No spam. No sales calls. Just occasional updates on our progress.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Minimal Footer */}
      <div className="py-8 text-center relative z-20">
        <p className="text-xs text-adaptive opacity-60">
          &copy; 2025 HAIDEN Technologies. All rights reserved.
        </p>
      </div>
    </div>
    </>
  )
}

export default App